import 'element-closest';
import 'whatwg-fetch';
import CustomEvent from 'custom-event';
import serialize from 'form-serialize';
import { Loader } from 'google-maps';
import { forEach } from '../../../includes/js/helpers';

// Initialize dataLayer.
window.dataLayer = window.dataLayer || [];

/**
 * How far a fit of the map's bounds is allowed to zoom in.
 *
 * Only ever binds when the markers barely span anything — one result, or several
 * at the same address — where the bounds have no area for `fitBounds` to work
 * from and it goes to the map's own maximum instead, close enough that the
 * streets stop being drawn at all (#31). Wide enough here to read the
 * surrounding streets and place the result among them.
 *
 * It caps the fit, not the map: the user can still zoom in past it by hand.
 *
 * @since 1.3.0
 *
 * @type {Number}
 */
const FIT_MAX_ZOOM = 14;

/**
 * Height to assume for an infowindow that can't be measured, in pixels.
 *
 * The bubble is ~200px tall at the desktop size and ~130px below `sm`. The
 * larger figure only ever feeds a `min()`, so it is safe at both.
 *
 * @since 1.3.0
 *
 * @type {Number}
 */
const EML_INFOWINDOW_HEIGHT = 200;

/**
 * How much clear map to leave around an open infowindow, in pixels. Enough to
 * read as a margin rather than as the bubble touching an edge.
 *
 * @since 1.3.0
 *
 * @type {Number}
 */
const EML_INFOWINDOW_MARGIN = 12;

/**
 * The gap Google leaves between the bottom of a bubble and the top of the pin it
 * belongs to — the tail, in pixels. Measures 11–13px and is not exposed by the
 * API, so it is carried here rather than derived.
 *
 * @since 1.3.0
 *
 * @type {Number}
 */
const EML_INFOWINDOW_TAIL = 12;

/**
 * How long to wait for the browser to produce a position, in milliseconds.
 *
 * The Geolocation API's own default is `Infinity`.
 *
 * @since 1.4.0
 *
 * @type {Number}
 */
const EML_GEOLOCATION_TIMEOUT = 10000;

/**
 * How old a stored position may be and still be used, in milliseconds.
 *
 * @since 1.4.0
 *
 * @type {Number}
 */
const EML_GEOLOCATION_MAX_AGE = 5 * 60 * 1000;

/**
 * When to lift the "loading results near your location" veil regardless, in
 * milliseconds. Longer than the timeout above, so it only comes into play where
 * that one can't: a permission prompt nobody answers.
 *
 * @since 1.4.0
 *
 * @type {Number}
 */
const EML_GEOLOCATION_VEIL_TIMEOUT = 15000;

/**
 * How far below the middle of the map to sit an open bubble and its pin, as a
 * share of the height on screen.
 *
 * Dead centre reads as centred but leaves the bubble nearer the top edge than
 * the pin is to the bottom, because the bubble is the taller half of the pair —
 * and the top edge is the busy one, with the header, the phone bar and the
 * search band all stacked over it. A little lower keeps the bubble clear of
 * them. Zero centres the pair exactly.
 *
 * @since 1.3.0
 *
 * @type {Number}
 */
const EML_INFOWINDOW_BIAS = 0.08;

/**
 * Object containing all Locator functionality.
 *
 * @since 1.0.0
 *
 * @type {Object}
 */
export default class EmLocator {
	/**
	 * Plugin settings.
	 * @type {Object}
	 */
	settings = { ...window.emLocatorSettings };

	/**
	 * Placeholder for all active map markers.
	 * @type {Array}
	 */
	markers = [];

	/**
	 * Placeholder for all active map infowindows.
	 * @type {Array}
	 */
	infowindows = [];

	activeInfowindow = false;

	hoverInfowindow = null;

	hoverCloseTimeout = null;

	/**
	 * Track the search input's previous value for comparison.
	 */
	currentSearchValue = '';

	/**
	 * What the current results are "of" — the city, state or zip the user searched
	 * for, or the picked autocomplete prediction. Named in the results summary, so
	 * it holds the term as entered rather than anything Google resolved it to.
	 *
	 * Empty until the first search, which is what keeps the summary hidden.
	 */
	searchLabel = '';

	/**
	 * Keep track of the current lat/lng coordinates.
	 */
	currentPosition = {
		lat: false,
		lng: false,
	};

	/**
	 * The active search radius. Held as state rather than passed around so every
	 * request path — search, geolocate, prediction select and load more — stays on
	 * the same radius. False only if the setting is missing, which drops the limit.
	 */
	currentDistance =
		parseFloat(window.emLocatorSettings.defaultRadius) || false;

	/**
	 * The radius the user actually picked, as opposed to `currentDistance`, which
	 * a search with no nearby results may have widened past it.
	 *
	 * Every new location search starts back here, so the widening applies to the
	 * search that needed it and not to the ones after it.
	 */
	preferredDistance =
		parseFloat(window.emLocatorSettings.defaultRadius) || false;

	/**
	 * The first radius that came back empty on this search, or false if the search
	 * never had to widen. Lets the summary say what was tried before it gave up on
	 * that radius — "No results found within 25 miles."
	 */
	widenedFrom = false;

	/**
	 * The active sort mode, 'distance' or 'procedures'. Held as state for the same
	 * reason as currentDistance, so pagination and every search path agree.
	 *
	 * Sort and radius are independent: radius narrows the result set, sort only
	 * reorders it.
	 */
	currentSort = 'distance';

	cluster = false;

	/**
	 * Where the search state is stashed between page views, scoped to the page the
	 * locator is on so two locators on different pages don't restore each other's
	 * search. sessionStorage rather than localStorage: the search should follow
	 * this tab's visit and not outlive it.
	 */
	stateKey = `emlSearchState:${window.location.pathname}`;

	stateTtl = 30 * 60 * 1000;

	/**
	 * True while a stashed search is being restored. Geolocation resolves on its
	 * own schedule and would otherwise replace the restored results with the
	 * default "near you" search.
	 */
	suppressGeolocationSearch = false;

	/**
	 * True when the next set of results is the answer to something the user just
	 * did — a search, a radius, a sort, a reset — and so is worth scrolling the
	 * mobile layout to. Read on landing pages only, where results that arrive on
	 * their own (the default search on load, the search replayed on the way back
	 * from a profile) have to leave the page where it is: the locator sits well
	 * down those templates, and moving the page to it — or, before this flag, to
	 * the top of the document — throws away wherever the reader was (#52, #53).
	 */
	scrollToResultsOnNextSearch = false;

	/**
	 * Keep track of pagination variables returned from REST Response.
	 */
	pagination = {
		totalPosts: 0,
		totalPages: 0,
		currentPage: 0,
		perPage: parseInt(window.emLocatorSettings.postsPerPage),
	};

	/**
	 * Which request is the current one.
	 *
	 * Every search path — search, prediction, geolocate, radius, sort, load more,
	 * restore — writes to the same `locations` array and `pagination` object when
	 * its response lands, and nothing stopped a superseded response from landing
	 * last. Two requests in flight at once then mixed their result sets: the list
	 * accumulated from one search while the count came from another, which is how
	 * the sidebar ended up holding more cards than the total it named (#40, #39).
	 *
	 * getPhysicians() takes a number off this counter on the way out and only
	 * writes state if it still holds the latest one, so a late response is
	 * discarded rather than merged.
	 */
	requestId = 0;

	markerIcon = {
		url: '/wp-content/plugins/aquablation-physician-locator/public/images/map-marker.svg',
	};

	markerIconHover = {
		url: '/wp-content/plugins/aquablation-physician-locator/public/images/map-marker-hover.svg',
	};

	/**
	 * Initialize all Locator functionality.
	 *
	 * @since 1.0.0
	 */
	constructor(locatorEl) {
		this.locatorEl = locatorEl;
		this.getGlobalElements();
		this.initMapLayout();
		this.initListLayout();
		this.initBasicAuth();
		this.initPagination();
		this.initGoogleMaps();
		this.initSearchStickyOffset();
		this.initMapViewportHeight();
		this.initEmbeddedBg();

		window.addEventListener('pagehide', this.handlePageHide);
		window.addEventListener('pageshow', this.handlePageShow);

		this.claimScrollRestoration();

		// ScrollTrigger releases the claim whenever it initialises or refreshes,
		// which it does on its own schedule through the load. Re-made across that
		// window so it is still ours when the browser would otherwise restore.
		[0, 100, 300, 600, 1000, 2000].forEach((delay) => {
			window.setTimeout(this.holdScrollRestoration, delay);
		});
	}

	/**
	 * Whether this instance has taken the scroll restoration off the browser.
	 *
	 * @since 1.3.0
	 */
	ownsScrollRestoration = false;

	/**
	 * Take the scroll restoration off the browser when there's a search to come
	 * back to.
	 *
	 * The results are fetched, so on the way back the page is short by everything
	 * below the locator until they land. Chrome and Android Chrome give up on
	 * restoring the scroll before that happens, which leaves the field clear for
	 * restoreScrollPosition(). iOS Safari restores later — after the results have
	 * rendered — and its restore landed on top of ours, which is why the fix held
	 * everywhere but iPhone (#53). `manual` stops it competing.
	 *
	 * Only claimed when a search is actually stashed, so an ordinary reload of a
	 * page nobody searched on still restores the way the browser intends.
	 *
	 * @since 1.3.0
	 */
	claimScrollRestoration = () => {
		if (!('scrollRestoration' in window.history)) {
			return;
		}

		if (!this.readSearchState()) {
			return;
		}

		this.ownsScrollRestoration = true;

		this.holdScrollRestoration();
	};

	/**
	 * Re-assert the claim, for as long as the restore is still going on.
	 *
	 * ScrollTrigger sets `scrollRestoration` back to `auto` itself, every time it
	 * initialises or refreshes, and it initialises after this class does — so the
	 * claim was being taken straight back off us and the browser went on
	 * restoring exactly as before. Nothing here can stop ScrollTrigger writing to
	 * it, so the claim is simply re-made after every point that provokes it.
	 *
	 * @since 1.3.0
	 */
	holdScrollRestoration = () => {
		if (!this.ownsScrollRestoration) {
			return;
		}

		try {
			window.history.scrollRestoration = 'manual';
		} catch (error) {
			// Leave the browser to it.
		}
	};

	/**
	 * Put the page back where it was when it comes out of the back/forward cache.
	 *
	 * A cached restore hands back the whole page, results and all, so nothing
	 * needs re-running — but with restoration set to `manual` the browser no
	 * longer returns the scroll either, so that part is ours to do. The page is
	 * already its full height here, so the offset applies straight away.
	 *
	 * @since 1.3.0
	 *
	 * @param {PageTransitionEvent} e
	 */
	handlePageShow = (e) => {
		if (!e.persisted) {
			return;
		}

		const state = this.readSearchState();

		if (!state || !state.scrollY) {
			return;
		}

		/**
		 * Through restoreScrollPosition() rather than a plain scrollTo, which is
		 * what this did at first. A cached restore lands the offset perfectly and
		 * then loses it: ScrollTrigger refreshes against the returning page and
		 * takes the scroll with it, after the one write here had already
		 * succeeded. The retry window holds the offset through that, exactly as it
		 * does for a fresh load (#53).
		 */
		this.restoreScrollPosition(state.scrollY);
	};

	initEmbeddedBg = () => {
		const bgBlock = this.locatorEl.closest(
			'.wp-block-emagine-background, .has-background, .wp-block-cover',
		);
		if (!bgBlock) return;

		const bgColor = getComputedStyle(bgBlock).backgroundColor;
		this.locatorEl.style.setProperty('--eml-block-bg', bgColor);
	};

	initSearchStickyOffset = () => {
		const siteHeader = document.querySelector('.site-header');
		const emlSearch = this.locatorEl.querySelector('.eml-search');
		const mapContainer = this.locatorEl.querySelector('.js-eml-map-container');
		const mobileSearch = mapContainer
			? mapContainer.querySelector('.eml-search')
			: null;

		if (!emlSearch) {
			return;
		}

		const update = () => {
			const headerBottom = siteHeader
				? siteHeader.getBoundingClientRect().bottom
				: 0;
			const searchBottom = headerBottom + emlSearch.offsetHeight;
			this.locatorEl.style.setProperty(
				'--eml-search-bottom',
				`${searchBottom}px`,
			);

			if (mobileSearch) {
				const mobileSearchTop =					parseFloat(getComputedStyle(mobileSearch).top) || 0;
				const mobileSearchBottom = mobileSearchTop + mobileSearch.offsetHeight;
				this.locatorEl.style.setProperty(
					'--eml-mobile-search-bottom',
					`${mobileSearchBottom}px`,
				);
			}
		};

		update();
		window.addEventListener('resize', update);

		if (document.body.classList.contains('em_landing_pages-template-default')) {
			window.addEventListener('load', update);
			if (document.fonts && document.fonts.ready) {
				document.fonts.ready.then(update);
			}
		}
	};

	/**
	 * Lift the desktop map so it starts already stuck under the site header, published
	 * as `--eml-map-offset`.
	 *
	 * The map is one viewport tall (minus the header) and sticky, so once it has stuck
	 * it fills the screen exactly. Before that the search band holds it further down
	 * the page, which puts its bottom edge — and so the Google controls and
	 * attribution that sit on it — below the fold.
	 *
	 * Rather than shrink the map to fit and grow it back on scroll, which relayouts the
	 * map on every frame, it keeps one height throughout and is pulled up by this
	 * offset so its bottom always rests on the bottom of the viewport. What the band
	 * would have covered is cropped instead by the module's own `overflow-y: clip`, and
	 * because the module scrolls normally that crop edge travels up the map on its own
	 * — the map is progressively revealed without a single style change while scrolling.
	 *
	 * Measured rather than derived in CSS because the band is pulled down over the map
	 * by a negative margin, so where the band ends is not where the map starts.
	 *
	 * @since 1.2.0
	 */
	initMapViewportHeight = () => {
		const siteHeader = document.querySelector('.site-header');
		const mapContainer = this.locatorEl.querySelector('.js-eml-map-container');

		if (!mapContainer) {
			return;
		}

		const isLandingPage = document.body.classList.contains(
			'em_landing_pages-template-default',
		);

		const update = () => {
			// The site header is sticky, so its bottom edge is where the map comes to
			// rest no matter how far the page has scrolled.
			const headerBottom = siteHeader
				? siteHeader.getBoundingClientRect().bottom
				: 0;
			// Document coordinates: the module isn't sticky, so this stays honest once
			// the page has scrolled and the map inside it has stuck.
			const mapRect = mapContainer.getBoundingClientRect();

			// Once the map is stuck — natively, from real scroll, or already lifted by
			// a previous call here — its rect no longer reports the un-stuck flow
			// position this formula needs, so re-deriving the offset now would just
			// fold the current scroll position into it and drag the map further up
			// each time this runs (e.g. re-running after a hash-link scroll already
			// landed on this section). Leave the existing offset alone when stuck;
			// only the height still needs to track the header. Landing pages only:
			// this module isn't re-measured elsewhere, so it can't regress other uses.
			const isStuck = isLandingPage
				&& Math.round(mapRect.top) <= Math.round(headerBottom) + 1;

			if (!isStuck) {
				const mapTop = mapRect.top + window.scrollY;
				const offset = Math.max(Math.round(mapTop - headerBottom), 0);
				this.locatorEl.style.setProperty('--eml-map-offset', `${-offset}px`);
			}

			// clientHeight rather than 100vh, which counts the horizontal scrollbar and
			// would leave the map's bottom edge hidden behind it.
			const height = Math.round(
				document.documentElement.clientHeight - headerBottom,
			);

			this.locatorEl.style.setProperty('--eml-map-height', `${height}px`);
		};

		update();
		window.addEventListener('resize', update);

		if (!isLandingPage) {
			return;
		}

		// The measurements above assume the header and the search band already have
		// their final size. Web fonts swapping in and images finishing after the
		// initial synchronous run can still shift both, so this re-measures once
		// things settle instead of leaving the map pulled up by a stale offset.
		window.addEventListener('load', update);
		if (document.fonts && document.fonts.ready) {
			document.fonts.ready.then(update);
		}
	};

	/**
	 * Fit the map to a set of bounds, keeping the markers within the part of the map
	 * that is actually on screen.
	 *
	 * On desktop the map is taller than its visible slice: it's lifted so its bottom
	 * edge meets the bottom of the viewport, and the module crops whatever ends up
	 * above itself (see `initMapViewportHeight`). Google knows nothing about that, so
	 * left alone it centres the markers in the full box and the crop hides them. The
	 * cropped depth is passed as top padding so they land in view instead.
	 *
	 * The crop shrinks to nothing as the page scrolls and the band clears the map, so
	 * this is measured per call rather than cached — but only ever at the moment of a
	 * fit, never while scrolling.
	 *
	 * @since 1.2.0
	 *
	 * @param {google.maps.LatLngBounds} bounds Bounds to fit.
	 */
	/**
	 * How much of the map's top edge the module is currently cropping, in pixels.
	 *
	 * See `fitMapBounds` for why the crop exists. Measured per call, never cached,
	 * because it shrinks to nothing as the page scrolls and the band clears the map.
	 *
	 * @since 1.3.0
	 *
	 * @return {Number} Cropped depth, or 0 when the whole map is on screen.
	 */
	getMapCroppedDepth = () => {
		const mapContainer = this.locatorEl.querySelector('.js-eml-map-container');
		const mapEl = this.locatorEl.querySelector('.js-eml-map');

		if (!mapContainer || !mapEl) {
			return 0;
		}

		const moduleTop = mapContainer.getBoundingClientRect().top;
		const mapTop = mapEl.getBoundingClientRect().top;

		return Math.max(Math.round(moduleTop - mapTop), 0);
	};

	fitMapBounds = (bounds) => {
		const cropped = this.getMapCroppedDepth();

		// Carried as a map option rather than as a correction afterwards, so a single
		// result settles at this zoom directly instead of racing to street level and
		// visibly springing back out.
		//
		// `fitBounds` reads the option when it is called, not when the camera arrives,
		// so putting it back on the next line still caps this fit and leaves nothing
		// behind: the user can zoom past it by hand, and `showClusterDetails`, which
		// sets a zoom of its own straight after fitting, is not clamped to it.
		this.map.setOptions({ maxZoom: FIT_MAX_ZOOM });

		// A bare fit when nothing is cropped, so the zoom Google picks is unchanged
		// everywhere the lift doesn't apply — mobile, and once scrolled.
		if (cropped > 0) {
			this.map.fitBounds(bounds, {
				top: cropped,
				right: 0,
				bottom: 0,
				left: 0,
			});
		} else {
			this.map.fitBounds(bounds);
		}

		this.map.setOptions({ maxZoom: null });
	};

	/**
	 * Get all relevent UI elements.
	 *
	 * @since 1.0.0
	 */
	getGlobalElements = () => {
		// Get search inputs from both containers
		const mainSearchEl = this.locatorEl.querySelector('.js-eml-search');
		const mapContainer = document.querySelector('.js-eml-map-container');
		const mapSearchEl = mapContainer
			? mapContainer.querySelector('.js-eml-search')
			: null;

		// Store both search inputs
		this.searchEls = [mainSearchEl, mapSearchEl].filter(Boolean);
		// Set the main search input as the default active one
		this.searchEl = mainSearchEl;

		// Update active search input when either input is focused
		this.searchEls.forEach((searchInput) => {
			if (searchInput) {
				searchInput.addEventListener('focus', () => {
					this.searchEl = searchInput;
				});
			}
		});

		const mainSearchBtns = this.locatorEl.querySelectorAll('.eml-search-btn');
		const mapSearchBtns = mapContainer
			? mapContainer.querySelectorAll('.eml-search-btn')
			: [];
		this.searchBtns = [...mainSearchBtns, ...mapSearchBtns];

		// Radius selects, one per search form (desktop and mobile).
		this.radiusEls = [
			this.locatorEl.querySelector('.js-eml-radius'),
			mapContainer ? mapContainer.querySelector('.js-eml-radius') : null,
		].filter(Boolean);

		// Sort toggle buttons — two per search form, and the form renders twice.
		this.sortEls = [
			...this.locatorEl.querySelectorAll('.js-eml-sort'),
			...(mapContainer ? mapContainer.querySelectorAll('.js-eml-sort') : []),
		];

		// "Clear All" buttons — one per search form (desktop and mobile).
		this.clearEls = [
			...this.locatorEl.querySelectorAll('.js-eml-clear'),
			...(mapContainer ? mapContainer.querySelectorAll('.js-eml-clear') : []),
		];

		this.predictionsEl = this.locatorEl.querySelector('.js-eml-predictions');

		// One alert box per search form. The desktop search's own box is inside an
		// `.eml-search` that collapses to `height: 0; overflow: hidden` below `md`, so
		// the mobile search inside the map carries a second one — writing to both is
		// what makes the message visible whichever form is on screen.
		this.searchAlertEls = [
			...this.locatorEl.querySelectorAll('.js-eml-search-alert'),
		];

		// Results summary — "Showing N Aquablation providers within R of {place}".
		this.resultsSummaryEl = this.locatorEl.querySelector(
			'.js-eml-results-summary',
		);
		this.resultsSummaryNoticeEl = this.locatorEl.querySelector(
			'.js-eml-results-summary-notice',
		);
		this.resultsSummaryCountEl = this.locatorEl.querySelector(
			'.js-eml-results-summary-count',
		);
		this.resultsSummaryLocationEl = this.locatorEl.querySelector(
			'.js-eml-results-summary-location',
		);
		this.resultsSummaryEditEl = this.locatorEl.querySelector(
			'.js-eml-results-summary-edit',
		);

		if (this.resultsSummaryEditEl) {
			this.resultsSummaryEditEl.addEventListener(
				'click',
				this.handleResultsSummaryEdit,
			);
		}

		this.paginationEls = this.locatorEl.querySelectorAll('.js-eml-pagination');
		this.loadMoreButtons = this.locatorEl.querySelectorAll(
			'.js-eml-load-more-button',
		);
	};

	/**
	 * Initialize the Map layout.
	 *
	 * @since 1.1.0
	 */
	initMapLayout = () => {
		this.mapContainerEl = this.locatorEl.querySelector('.js-eml-map-container');
		if (this.isMapEnabled()) {
			this.mapLocationsEl = this.locatorEl.querySelector(
				'.js-eml-map-locations',
			);
			this.mapLocationDetailsEl = this.locatorEl.querySelector(
				'.js-eml-map-location-details',
			);
			this.mapBackButtonEl = this.locatorEl.querySelector(
				'.js-eml-map-back-button',
			);
			this.mapEl = this.locatorEl.querySelector('.js-eml-map');
			this.mapSidebarInnerEl = this.locatorEl.querySelector(
				'.eml-map__sidebar-inner',
			);
			this.mapLayoutControl = this.locatorEl.querySelector(
				'.js-eml-map-layout-control',
			);

			if (this.mapLayoutControl) {
				this.mapLayoutControl.addEventListener('click', this.showMapLayout);
			}

			this.mapEl.addEventListener(
				'wheel',
				(e) => {
					e.preventDefault();
					if (this.mapSidebarInnerEl) {
						this.mapSidebarInnerEl.scrollTop += e.deltaY;
					}
				},
				{ passive: false },
			);
		}
	};

	/**
	 * Show the map layout and hide the list layout.
	 *
	 * @since 1.0.0
	 */
	showMapLayout = () => {
		this.locatorEl.classList.add('show-map');
		this.locatorEl.classList.remove('show-list');
		this.fitMapBounds(this.map.bounds);
	};

	/**
	 * Initialize the List layout.
	 *
	 * @since 1.1.0
	 */
	initListLayout = () => {
		this.listLocationsEl = this.locatorEl.querySelector(
			'.js-eml-list-locations',
		);
		this.listLayoutControl = this.locatorEl.querySelector(
			'.js-eml-list-layout-control',
		);

		if (this.listLayoutControl) {
			this.listLayoutControl.addEventListener('click', this.showListLayout);
		}
	};

	/**
	 * Show the list layout and hide the map layout.
	 *
	 * @since 1.0.0
	 */
	showListLayout = () => {
		this.locatorEl.classList.remove('show-map');
		this.locatorEl.classList.add('show-list');
	};

	/**
	 * Update apiUrl with basic auth credientials, if available.
	 *
	 * @since 1.0.0
	 */
	initBasicAuth = () => {
		const { basicAuthUsername: user, basicAuthPassword: pass } = this.settings;
		if (user && pass) {
			this.fetchHeaders = {
				headers: {
					Authorization: `Basic ${btoa(`${user}:${pass}`)}`,
				},
			};
		} else {
			this.fetchHeaders = {};
		}
	};

	/**
	 * Check plugin settings to see which sharing methods are enabled,
	 * and setup booleans and event handlers.
	 *
	 * @since 1.0.0
	 */
	initShareMethods = () => {
		if (this.settings.shareMethods) {
			/**
			 * Extract share methods for easier use.
			 */
			const { download, email, print } = this.settings.shareMethods;

			/**
			 * Create booleans that tell which share methods are enabled for other functions to access.
			 */
			this.downloadEnabled = download !== undefined || false;
			this.emailEnabled = email !== undefined || false;
			this.printEnabled = print !== undefined || false;
			this.shareEnabled =				this.downloadEnabled || this.emailEnabled || this.printEnabled;

			/**
			 * Get all share controls and add event listeners.
			 */
			this.shareControls = this.locatorEl.querySelectorAll(
				'.js-eml-share-control',
			);
			forEach(this.shareControls, (i, control) => {
				control.addEventListener('click', this.handleShareControlClick);
			});

			if (this.emailEnabled) {
				this.emailFormContainer = this.locatorEl.querySelector(
					'.js-eml-email-form-container',
				);
				this.emailFormContainer.addEventListener(
					'click',
					this.handleEmailFormClose,
				);

				this.emailFormCloseButton = this.locatorEl.querySelector(
					'.js-eml-email-form-close-button',
				);
				this.emailFormCloseButton.addEventListener(
					'click',
					this.handleEmailFormClose,
				);

				this.emailForm = this.locatorEl.querySelector('.js-eml-email-form');
				this.emailForm.addEventListener('submit', this.handleEmailFormSubmit);

				this.locatorEl.addEventListener(
					'click',
					this.handleEmailFormControlClick,
				);

				this.emailFormAlert = this.locatorEl.querySelector(
					'.js-eml-email-form-alert',
				);
			}
		}
	};

	/**
	 * Initialize pagination functionality.
	 *
	 * @since 1.0.0
	 */
	initPagination = () => {
		forEach(this.loadMoreButtons, (i, loadMoreButton) => {
			if (!loadMoreButton.classList.contains('eml-load-cluster-locations')) {
				loadMoreButton.addEventListener('click', this.handleLoadMore);
			}
		});
	};

	/**
	 * Initialize Google Maps.
	 *
	 * @since 1.0.0
	 */
	initGoogleMaps = async () => {
		const options = {
			libraries: ['places'],
		};
		const loader = new Loader(this.settings.googleMapsApiKey, options);
		try {
			this.google = await loader.load();
			this.initSearch();

			// A search left behind earlier in this visit outranks the default one,
			// so coming back from a profile lands on the same results and filters.
			const savedState = this.readSearchState();

			if (savedState) {
				this.restoreSearchState(savedState);
			} else {
				this.runDefaultSearch();
			}

			// Geolocation resolves after the first results land, and re-runs the
			// search itself once it has a position.
			if (
				!this.locatorEl.dataset.centerLat
				|| !this.locatorEl.dataset.centerLng
			) {
				this.geolocate();
			}
			if (this.isMapEnabled()) {
				this.initMap();
			}
		} catch (error) {
			console.error('initGoogleMaps: Error loading Google Maps API:', error);
		}
	};

	/**
	 * Initialize Google's AutocompleteService and Geocoder classes for later use,
	 * and register all search related event listeners.
	 *
	 * @since 1.0.0
	 */
	initSearch = () => {
		this.geocoder = new this.google.maps.Geocoder();

		// Add event listeners to all search inputs
		this.searchEls.forEach((searchInput) => {
			if (searchInput) {
				// Add input event listener to track value changes
				searchInput.addEventListener('input', (event) => {
					event.target.dataset.lastValue = event.target.value;
				});

				// Add focus/blur handlers
				searchInput.addEventListener('focusin', this.handleInputFocus);
				searchInput.addEventListener('blur', this.handleInputBlur);

				// Add keypress handler for Enter key
				searchInput.addEventListener('keypress', (event) => {
					if (event.key === 'Enter') {
						event.preventDefault();
						const container = event.target.closest(
							'.eml-search__field-container',
						);
						if (container) {
							const searchBtn = container.querySelector('.eml-search-btn');
							if (searchBtn) {
								this.handleLocationSearch({ target: searchBtn });
							}
						}
					}
				});
			}
		});

		// Add click handlers to search buttons
		forEach(this.searchBtns, (i, searchBtn) => {
			searchBtn.addEventListener('click', (event) => {
				event.preventDefault();
				event.stopPropagation();

				// Find the associated search input
				const container = searchBtn.closest('.eml-search__field-container');
				if (container) {
					const searchInput = container.querySelector('.js-eml-search');
					if (searchInput) {
						searchInput.dataset.lastValue = searchInput.value;
					}
				}

				this.handleLocationSearch(event);
			});
		});

		// Add change handlers to the radius selects
		this.radiusEls.forEach((radiusEl) => {
			radiusEl.addEventListener('change', this.handleRadiusChange);
		});

		// Add click handlers to the sort toggle buttons
		this.sortEls.forEach((sortEl) => {
			sortEl.addEventListener('click', this.handleSortChange);
		});

		// Add click handlers to the "Clear All" buttons
		this.clearEls.forEach((clearEl) => {
			clearEl.addEventListener('click', this.handleClearAll);
		});

		document.addEventListener('keydown', this.handleKeyboardNavigation);
		document.addEventListener('mousedown', this.handlePredictionClick);
		document.addEventListener('touchstart', this.handlePredictionClick);
	};

	/**
	 * Update the active search radius and re-run the current search.
	 *
	 * @since 1.2.0
	 *
	 * @param {Event} event The select's change event.
	 */
	handleRadiusChange = (event) => {
		const { value } = event.target;

		// An explicit pick becomes the preference every later search starts from,
		// and starts the widening over from that radius.
		this.preferredDistance = parseFloat(value) || false;
		this.widenedFrom = false;

		// Keeps the desktop and mobile selects in sync too.
		this.setDistance(this.preferredDistance);

		// Only re-search if we already have an origin. Otherwise the new radius is
		// picked up by the next search.
		if (this.currentPosition.lat && this.currentPosition.lng) {
			this.scrollToResultsOnNextSearch = true;

			this.getPhysicians(
				this.currentPosition.lat,
				this.currentPosition.lng,
				1,
			);

			return;
		}

		// No origin, so no re-search — but the radius the summary names has still
		// changed, and without this the sentence kept the old one while the select
		// showed the new. That mismatch is why the radius looked like it updated on
		// some searches and not others (#9).
		this.updateResultsSummary();

		// A radius is only meaningful from somewhere, and there is nowhere yet.
		this.promptForSearchLocation(event);
	};

	/**
	 * Update the active sort mode and re-run the current search.
	 *
	 * @since 1.3.0
	 *
	 * @param {Event} event The button's click event.
	 */
	handleSortChange = (event) => {
		// The click can land on the inner svg or label, so resolve to the button.
		const button = event.target.closest('.js-eml-sort');

		if (!button) {
			return;
		}

		event.preventDefault();

		const { sort } = button.dataset;

		if (!sort || sort === this.currentSort) {
			return;
		}

		this.currentSort = sort;

		// Keep the desktop and mobile toggles in sync.
		this.sortEls.forEach((sortEl) => {
			const isActive = sortEl.dataset.sort === sort;
			sortEl.classList.toggle('is-active', isActive);
			sortEl.setAttribute('aria-pressed', isActive ? 'true' : 'false');
		});

		// Only re-search if we already have an origin. Otherwise the new sort is
		// picked up by the next search.
		if (this.currentPosition.lat && this.currentPosition.lng) {
			this.scrollToResultsOnNextSearch = true;

			this.getPhysicians(
				this.currentPosition.lat,
				this.currentPosition.lng,
				1,
			);

			return;
		}

		// Both sorts rank against an origin, and there is nowhere yet.
		this.promptForSearchLocation(event);
	};

	/**
	 * Reset every search control to its default and re-run the default search.
	 *
	 * @since 1.3.0
	 *
	 * @param {Event} event The button's click event.
	 */
	handleClearAll = (event) => {
		event.preventDefault();

		// Clear the search inputs, their predictions and any alert. Dropping the
		// search label hides the results summary until the next search names a
		// place — runDefaultSearch() may set it again by way of useCurrentPosition().
		this.currentSearchValue = '';
		this.searchLabel = '';

		this.searchEls.forEach((searchEl) => {
			searchEl.value = '';
			delete searchEl.dataset.lastValue;
			searchEl.classList.remove('has-current-location-message', 'focused');
		});

		if (this.predictionsEl) {
			this.predictionsEl.innerHTML = '';
			this.predictionsEl.classList.remove('active');
		}

		this.setSearchAlert();

		// Radius back to the configured default, including any widening a previous
		// search applied on top of it.
		this.preferredDistance = parseFloat(this.settings.defaultRadius) || false;
		this.widenedFrom = false;
		this.setDistance(this.preferredDistance);

		// Sort back to nearest-first.
		this.currentSort = 'distance';

		this.sortEls.forEach((sortEl) => {
			const isActive = sortEl.dataset.sort === 'distance';
			sortEl.classList.toggle('is-active', isActive);
			sortEl.setAttribute('aria-pressed', isActive ? 'true' : 'false');
		});

		// Leave the location details panel behind.
		if (this.activeInfowindow) {
			this.activeInfowindow.close();
			this.activeInfowindow = false;
		}

		if (this.isMapEnabled()) {
			this.mapContainerEl.classList.remove('has-details');
		}

		// A cluster view hands the load more buttons to its own handler, so put them
		// back before the default results land.
		forEach(this.loadMoreButtons, (i, loadMoreButton) => {
			loadMoreButton.removeEventListener('click', this.handleClusterLoadMore);
			loadMoreButton.classList.remove('eml-load-cluster-locations');
			loadMoreButton.addEventListener('click', this.handleLoadMore);
			loadMoreButton.innerHTML = 'Load more';
		});

		this.cluster = false;
		this.currentPosition = { lat: false, lng: false };

		// Clearing is the user saying they're done with that search, so there's
		// nothing left to restore on the way back.
		this.clearSearchState();
		this.suppressGeolocationSearch = false;
		this.scrollToResultsOnNextSearch = true;

		this.runDefaultSearch();
	};

	/**
	 * Run the search the locator loads with: the shortcode's own center if it has
	 * one, the visitor's position once geolocation has resolved it, or the
	 * unfiltered list until then.
	 *
	 * @since 1.3.0
	 */
	runDefaultSearch = () => {
		const { centerLat, centerLng } = this.locatorEl.dataset;

		if (centerLat && centerLng) {
			this.getPhysicians(parseFloat(centerLat), parseFloat(centerLng), 1);
		} else if (this.userPosition) {
			this.useCurrentPosition();
		} else {
			this.getPhysicians();
		}
	};

	/**
	 * Stash the current search so returning to the locator — most often with the
	 * browser's back button, after a physician or location profile — picks up
	 * where the user left off instead of resetting to the defaults.
	 *
	 * Called after every set of results lands, which is the one place every search
	 * path (search, prediction, geolocate, radius, sort, load more) passes through.
	 *
	 * @since 1.3.0
	 */
	saveSearchState = () => {
		// A cluster view is a transient slice of the sidebar rather than a search,
		// so there's nothing meaningful to come back to.
		if (this.cluster) {
			return;
		}

		const searchEl = this.searchEls[0];

		const state = {
			savedAt: Date.now(),
			searchLabel: this.searchLabel,
			searchValue: searchEl ? searchEl.value : '',
			isCurrentLocation: searchEl
				? searchEl.classList.contains('has-current-location-message')
				: false,
			position: this.currentPosition,
			preferredDistance: this.preferredDistance,
			currentDistance: this.currentDistance,
			widenedFrom: this.widenedFrom,
			sort: this.currentSort,
			page: this.pagination.currentPage,
			scrollY: window.pageYOffset,
		};

		try {
			window.sessionStorage.setItem(this.stateKey, JSON.stringify(state));
		} catch (error) {
			// Private browsing or a full quota — the locator just won't restore.
		}
	};

	/**
	 * The stashed search, if there is one and it hasn't gone stale.
	 *
	 * @since 1.3.0
	 *
	 * @return {Object|Boolean} The saved state, or false.
	 */
	readSearchState = () => {
		let state;

		try {
			state = JSON.parse(window.sessionStorage.getItem(this.stateKey));
		} catch (error) {
			return false;
		}

		if (!state || !state.savedAt) {
			return false;
		}

		if (Date.now() - state.savedAt > this.stateTtl) {
			this.clearSearchState();
			return false;
		}

		return state;
	};

	/**
	 * Drop the stashed search, so the next visit starts from the defaults.
	 *
	 * @since 1.3.0
	 */
	clearSearchState = () => {
		try {
			window.sessionStorage.removeItem(this.stateKey);
		} catch (error) {
			// Nothing to clear if storage isn't available.
		}
	};

	/**
	 * Put the controls back the way the user left them and re-run their search.
	 *
	 * The results themselves aren't stored — only what they were a search for —
	 * so the list is always fresh even though it looks like it was never left.
	 *
	 * @since 1.3.0
	 *
	 * @param {Object} state A state object from readSearchState().
	 */
	restoreSearchState = (state) => {
		this.suppressGeolocationSearch = true;

		this.searchLabel = state.searchLabel || '';
		this.currentSearchValue = '';
		this.preferredDistance = state.preferredDistance || false;
		this.widenedFrom = state.widenedFrom || false;
		// Ignore a stashed 'procedures' when procedures have since been turned off:
		// the state outlives the setting change by up to the 30-minute TTL, and the
		// control it came from is no longer on the page to change it back.
		this.currentSort = this.settings.proceduresEnabled
			? state.sort || 'distance'
			: 'distance';

		// Radius selects, on the search value that produced these results rather
		// than the preference, so the control matches the list.
		this.setDistance(state.currentDistance || false);

		// Sort toggles, desktop and mobile.
		this.sortEls.forEach((sortEl) => {
			const isActive = sortEl.dataset.sort === this.currentSort;
			sortEl.classList.toggle('is-active', isActive);
			sortEl.setAttribute('aria-pressed', isActive ? 'true' : 'false');
		});

		// Search inputs, including the "using your location" treatment when that's
		// what the results came from.
		this.searchEls.forEach((searchEl) => {
			searchEl.value = state.searchValue || '';
			searchEl.classList.toggle(
				'has-current-location-message',
				!!state.isCurrentLocation,
			);
		});

		/**
		 * A search with no position behind it is still a search worth coming back
		 * to. The unfiltered list is what a visitor sees whenever geolocation is
		 * declined or never answered and the shortcode carries no centre of its
		 * own — most of a landing page's traffic — and it is fetched like any
		 * other, so the page is just as short on the way back and the scroll has
		 * just as far to fall. Treating it as nothing to restore was why the
		 * position held everywhere location was granted and nowhere it wasn't
		 * (#53). getPhysicians() reads a false lat/lng as the unfiltered list, so
		 * the replay below needs no special case.
		 */
		const { lat, lng } = state.position || { lat: false, lng: false };

		/**
		 * Walk back up through the pages the user had loaded. Each request
		 * concatenates onto the last, the same way "Load more" does, so the list
		 * comes back at the length it was rather than at the first page.
		 */
		const pages = Math.min(Math.max(parseInt(state.page, 10) || 1, 1), 10);

		/**
		 * The replay is up to ten requests long and each one reads the radius and
		 * sort as they stand when it goes out, so a search, radius or sort change
		 * part way through used to leave the rest of the chain fetching pages of a
		 * list that no longer existed — and appending them to the user's new one.
		 * `mine` is the request number the replay last owned; anything else holding
		 * it means the user has taken over and the remaining pages are abandoned.
		 */
		let mine;

		/**
		 * getPhysicians() claims its request number synchronously, before it goes
		 * anywhere near the network, so reading the counter straight after the call
		 * reads the number belonging to that call. It has to be recorded here and
		 * not once the response lands: by then the user may already have claimed a
		 * newer number, and adopting that would be the replay declaring itself
		 * current again on the strength of a request it doesn't own.
		 */
		const fetchPage = (page) => {
			const pending = this.getPhysicians(lat, lng, page);

			mine = this.requestId;

			return pending;
		};

		const remainingPages = [];

		for (let page = 2; page <= pages; page += 1) {
			remainingPages.push(page);
		}

		const request = remainingPages.reduce(
			(chain, page) => chain.then(() => {
				if (this.requestId !== mine) {
					return null;
				}

				return fetchPage(page);
			}),
			fetchPage(1),
		);

		return request.then(() => {
			// Putting the user back where the replay left off is only right if the
			// replay is still what's on screen.
			if (this.requestId !== mine) {
				return null;
			}

			this.restoreScrollPosition(state.scrollY);

			return null;
		});
	};

	/**
	 * Return the page to where the user was reading. Results arrive after the
	 * browser has already given up on restoring the scroll itself, so the page is
	 * too short at that point for it to have landed anywhere but the top.
	 *
	 * @since 1.3.0
	 *
	 * @param {Number} scrollY The offset to return to.
	 */
	restoreScrollPosition = (scrollY) => {
		if (!scrollY) {
			return;
		}

		/**
		 * The offset is re-applied over the next few hundred milliseconds rather
		 * than once. Marker icons, photos and the map itself all settle after the
		 * results land, and every one of them changes the page height under the
		 * scroll — most visibly on iOS Safari, where the page was still growing
		 * well after the first frame (#53). Any real scroll from the user ends it,
		 * so the retries can never fight someone reading the page.
		 */
		let cancelled = false;
		let correct;

		const stop = () => {
			cancelled = true;

			['wheel', 'touchstart', 'keydown'].forEach((type) => {
				window.removeEventListener(type, stop);
			});

			window.removeEventListener('scroll', correct);
		};

		['wheel', 'touchstart', 'keydown'].forEach((type) => {
			window.addEventListener(type, stop, { once: true, passive: true });
		});

		/**
		 * Something else moving the page is the thing worth reacting to, and a
		 * fixed set of retries can only guess at when that happens. A landing
		 * page's form embed scrolls its parent to the top as it loads, and how
		 * long that takes depends on the network — inside the window on a local
		 * site, outside it on a real device against a remote one, which is why a
		 * shorter window held locally and let go everywhere else. Watching for
		 * the jump catches it whenever it lands.
		 *
		 * The listeners above run first, so anything the reader does themselves
		 * has already cancelled this by the time it could fire.
		 */
		let corrections = 0;

		correct = () => {
			if (cancelled || Math.abs(window.pageYOffset - scrollY) < 200) {
				return;
			}

			/**
			 * Capped, because each correction scrolls the page and so provokes the
			 * event that called it. Normally the next one reads as already in place
			 * and stops there, but a target the page has become too short to reach
			 * never does, and that would spin until the watch expires.
			 */
			corrections += 1;

			if (corrections > 8) {
				stop();
				return;
			}

			window.scrollTo(0, scrollY);
		};

		window.addEventListener('scroll', correct, { passive: true });

		const apply = () => {
			if (cancelled) {
				return;
			}

			// The pinned map is measured against the old, shorter page.
			if (this.scrollTrigger) {
				this.scrollTrigger.kill();
			}

			if (typeof this.initScrollTrigger === 'function') {
				this.initScrollTrigger();
			}

			// Straight after the re-init, which is one of the moments that hands
			// the restoration back to the browser.
			this.holdScrollRestoration();

			window.scrollTo(0, scrollY);
		};

		window.requestAnimationFrame(apply);

		[100, 300, 600, 1000].forEach((delay) => {
			window.setTimeout(apply, delay);
		});

		window.setTimeout(stop, 4000);
	};

	/**
	 * Catch the scroll position on the way out, since leaving the page is the last
	 * chance to record where the user was before they come back to it.
	 *
	 * @since 1.3.0
	 */
	handlePageHide = () => {
		const state = this.readSearchState();

		if (!state) {
			return;
		}

		state.scrollY = window.pageYOffset;

		try {
			window.sessionStorage.setItem(this.stateKey, JSON.stringify(state));
		} catch (error) {
			// Leave the stored scroll position as it was.
		}
	};

	/**
	 * Initialize the map.
	 *
	 * @since 1.0.0
	 */
	initMap = () => {
		const centerLat = this.locatorEl.dataset.centerLat
			? parseFloat(this.locatorEl.dataset.centerLat)
			: this.settings.centerLat;
		const centerLng = this.locatorEl.dataset.centerLng
			? parseFloat(this.locatorEl.dataset.centerLng)
			: this.settings.centerLng;
		const centerZoom = this.locatorEl.dataset.centerZoom
			? parseInt(this.locatorEl.dataset.centerZoom)
			: parseInt(this.settings.zoomLevel);

		const mapSettings = {
			zoom: centerZoom,
			center: new this.google.maps.LatLng(centerLat, centerLng),
			gestureHandling: 'none',
			mapTypeControlOptions: {
				position: this.google.maps.ControlPosition.BOTTOM_LEFT,
			},
			fullscreenControl: false,
		};

		if (this.settings.googleMapsTheme) {
			mapSettings.styles = JSON.parse(this.settings.googleMapsTheme);
		}

		this.map = new this.google.maps.Map(this.mapEl, mapSettings);

		this.updateMarkerIconSizes();

		this.markerIconHover = {
			url: this.markerIconHover.url,
			scaledSize: this.markerIcon.scaledSize,
			anchor: this.markerIcon.anchor,
		};

		this.google.maps.event.addListener(this.map, 'idle', () => {
			this.mapCenter = this.map.getCenter();
		});

		this.google.maps.event.addListener(window, 'resize', () => {
			this.google.maps.event.trigger(this.map, 'resize');
		});

		this.google.maps.event.addListener(this.map, 'resize', this.recenterMap);

		/**
		 * Setup map sidebar events.
		 */
		// this.mapLocationsEl.addEventListener('click', this.handleMapLocationClick);
		this.mapLocationsEl.addEventListener(
			'mouseover',
			this.handleMapLocationHover,
		);
		this.mapLocationsEl.addEventListener(
			'mouseout',
			this.handleMapLocationLeave,
		);
		this.mapLocationsEl.addEventListener('touchend', this.handleCardTouch);
		this.mapBackButtonEl.addEventListener('click', this.hideMapLocationDetails);

		this.mapEl.addEventListener('mouseover', (e) => {
			if (e.target.closest('.gm-style-iw-c')) {
				this.cancelHoverClose();
			}
		});

		this.mapEl.addEventListener('mouseout', (e) => {
			if (
				e.target.closest('.gm-style-iw-c')
				&& !e.relatedTarget?.closest('.gm-style-iw-c')
			) {
				this.scheduleHoverClose();
			}
		});
	};

	/**
	 * Use the Geolocation API to get the user's current location in order to
	 * center the map, and set the Autocomplete bounds. Position is stored in
	 * localStorage, so that on subsequent views, the map will instantly load
	 * in the right location.
	 *
	 * @since 1.0.0
	 */
	geolocate = () => {
		/**
		 * Make sure the user's browser supports geolocation before continuing.
		 */
		if (navigator.geolocation) {
			// Track changes to geolocation permission.
			navigator.permissions.query({ name: 'geolocation' }).then((result) => {
				if (result.state === 'prompt') {
					window.dataLayer.push({
						event: 'FauGeolocationPromptShown',
						prompt_event: 'location_prompt_shown',
					});
				}

				result.onchange = () => {
					let dataLayerEvent;

					// Add conditional checks for when user 1) hasn't interacted with the prompt, 2) has allowed geolocation, 3) has denied geolocation.
					if (result.state === 'prompt') {
						dataLayerEvent = {
							event: 'FauGeolocationPromptShown',
							prompt_event: 'location_prompt_shown',
						};
					} else if (result.state === 'granted') {
						dataLayerEvent = {
							event: 'FauGeolocationAllowed',
							prompt_event: 'location_allowed',
						};
					} else if (result.state === 'denied') {
						dataLayerEvent = {
							event: 'FauGeolocationDenied',
							prompt_event: 'location_denied',
						};
					}

					window.dataLayer.push(dataLayerEvent);
				};
			});

			if (localStorage.emlUserPosition) {
				/**
				 * Check if emlUserPosition is in localStorage, and if so,
				 * use that to show locations close to the user's location.
				 */
				this.userPosition = JSON.parse(localStorage.emlUserPosition);

				// Keep the position for "Clear All" to fall back on, but leave a
				// restored search alone — the user picked it, this is only a default.
				if (!this.suppressGeolocationSearch) {
					this.useCurrentPosition();
				}
			} else {
				/**
				 * Add loading class and message to components. A restored search
				 * already has results and a search term in the input, so it isn't
				 * waiting on geolocation and mustn't be overwritten by it.
				 */
				if (!this.suppressGeolocationSearch) {
					this.searchEl.value = 'Loading results near your location...';
					this.searchEl.setAttribute('disabled', 'disabled');
					this.locatorEl.classList.add('loading');
				}

				/**
				 * Remove loading class and message from components.
				 */
				const stopLoading = () => {
					clearTimeout(this.geolocationVeilTimeout);

					if (this.suppressGeolocationSearch) {
						return;
					}

					this.searchEl.value = '';
					this.searchEl.removeAttribute('disabled');
					this.locatorEl.classList.remove('loading');
				};

				/**
				 * Lift the veil even if geolocation answers neither way.
				 *
				 * `getCurrentPosition` is the only thing that clears it, and a machine
				 * that grants the permission without ever producing a fix — a cloud
				 * test VM, a desktop with location services off — leaves both
				 * callbacks unfired. The `timeout` below covers that, but only once
				 * the permission is settled: the clock doesn't start while the prompt
				 * is still on screen, so a prompt left unanswered would hold the veil
				 * over results that are already listed underneath it.
				 */
				this.geolocationVeilTimeout = setTimeout(
					stopLoading,
					EML_GEOLOCATION_VEIL_TIMEOUT,
				);

				/**
				 * Function to run when geolocation has run successfully.
				 * @param {Position} position
				 */
				const success = (position) => {
					stopLoading();

					/**
					 * Take only the values we need from the 'position' object
					 */
					this.userPosition = {
						lat: position.coords.latitude,
						lng: position.coords.longitude,
						accuracy: position.coords.accuracy,
					};

					/**
					 * Save the user's current position for later use.
					 */
					localStorage.setItem(
						'emlUserPosition',
						JSON.stringify(this.userPosition),
					);

					// See the localStorage branch above: a restored search stands.
					if (this.suppressGeolocationSearch) {
						return;
					}

					this.currentPosition = {
						lat: position.coords.latitude,
						lng: position.coords.longitude,
					};

					this.useCurrentPosition();
				};

				/**
				 * Function to run when geolocation fails.
				 * @param {PositionError} error
				 */
				const error = () => {
					stopLoading();
				};

				/**
				 * A timeout, because the default is `Infinity`: a browser that never
				 * resolves a position calls neither callback, and the veil above has
				 * nothing else to lift it. A stored fix up to five minutes old is
				 * accepted rather than waiting for a fresh one.
				 */
				navigator.geolocation.getCurrentPosition(success, error, {
					timeout: EML_GEOLOCATION_TIMEOUT,
					maximumAge: EML_GEOLOCATION_MAX_AGE,
				});
			}
		} else {
			console.warn('geolocate: Browser does not support geolocation');
		}
	};

	/**
	 * Update the map and results using the user's current location.
	 *
	 * @since 1.0.0
	 */
	useCurrentPosition = () => {
		// Geolocation gives coordinates, not a place name, so the summary says
		// where the results are from in the only terms available.
		this.beginNewSearch('your location');

		/**
		 * Deciding to search from here is what makes this the origin, so it is
		 * recorded now rather than when the results land.
		 *
		 * getPhysicians() sets it too, but only on the way back. That left a window
		 * — a couple of hundred milliseconds on a fast connection, seconds from a
		 * phone on 3G — where the module had no origin to its name, and a radius or
		 * sort change during it answered "please enter a location" to someone who
		 * had just shared theirs. The stored-position path made it worse by never
		 * raising the loading veil, so the module looked ready throughout.
		 */
		this.currentPosition = {
			lat: this.userPosition.lat,
			lng: this.userPosition.lng,
		};

		this.getPhysicians(this.userPosition.lat, this.userPosition.lng);
		this.searchEl.value = 'Currently using your location';
		this.searchEl.classList.add('has-current-location-message');
	};

	/**
	 * Clear out the "current location" placeholder message from the input on focus.
	 *
	 * @since 1.0.0
	 */
	handleInputFocus = () => {
		this.searchEl.classList.add('focused');
		if (this.searchEl.classList.contains('has-current-location-message')) {
			this.searchEl.value = '';
			this.searchEl.classList.remove('has-current-location-message');
			this.setSearchAlert();
		}
	};

	/**
	 * Remove classes from the searchEl and predictionsEl when the searchEl loses focus.
	 *
	 * @since 1.0.0
	 */
	handleInputBlur = () => {
		// Blur is not a search, so there is nothing to announce here — the results
		// list owns the empty state (see `getNoResultsAlert`). This used to render the
		// no-results message whenever the *predictions* dropdown had items, which is
		// what `hasResults` actually measured, and the alert box sits under the search
		// panel — the card is pulled 58px down over it — so the message surfaced as a
		// stray line of text behind the input on every search (#12).
		this.setSearchAlert();

		setTimeout(() => {
			this.searchEl.classList.remove('focused');
			this.predictionsEl.classList.remove('active');
		}, 150);
	};

	/**
	 * Check to see if a prediction from the autocomplete dropdown was clicked,
	 * and if so, trigger this.handlePredictionSelect(), passing the event target.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	handlePredictionClick = (e) => {
		if (e.target.classList.contains('js-ac-prediction')) {
			this.handlePredictionSelect(e.target);
		}
	};

	/**
	 * Enabled keyboard navigation of the autocomplete dropdown. User's can use the up and down
	 * arrows to highlight the prediction, and then can hit 'enter' to select it. Hitting the
	 * 'escape' key will close the dropdown.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	handleKeyboardNavigation = (e) => {
		if (this.searchEl.classList.contains('focused')) {
			switch (e.key) {
				case 'ArrowDown':
				case 'ArrowUp': {
					e.preventDefault();
					const predictionEls =						this.predictionsEl.querySelectorAll('.js-ac-prediction');
					const activePredictionEl = this.predictionsEl.querySelector(
						'.js-ac-prediction.active',
					);

					let activeIndex = -1;
					forEach(predictionEls, (i, predictionEl) => {
						if (predictionEl === activePredictionEl) {
							activeIndex = i;
						}
					});

					/**
					 * Update activeIndex according to which arrow key was pressed.
					 */
					if (e.key === 'ArrowDown') {
						if (activeIndex === -1) {
							activeIndex = 0;
						} else if (activeIndex !== predictionEls.length - 1) {
							activeIndex += 1;
						}
					} else if (activeIndex > 0) {
						activeIndex -= 1;
					}

					/**
					 * Remove the active class from the previous prediction, and add it to the new prediction.
					 */
					activePredictionEl.classList.remove('active');
					predictionEls[activeIndex].classList.add('active');
					break;
				}

				case 'Enter': {
					// Only commits a prediction the arrow keys highlighted. The plain
					// Enter-runs-the-search case belongs to the input's own keypress
					// handler (see initSearch) — running it here as well sent two
					// geocodes and two renders per keystroke.
					const activePredictionEl = this.predictionsEl.querySelector(
						'.js-ac-prediction.active',
					);

					if (activePredictionEl) {
						e.preventDefault();
						this.handlePredictionSelect(activePredictionEl);
					}

					break;
				}

				case 'Escape': {
					this.searchEl.value = '';
					this.searchEl.blur();
					break;
				}
				default: {
					break;
				}
			}
		}
	};

	/**
	 * Trigger a call to this.getPhysicians() when a prediction from the autocomplete dropdown
	 * is selected. If the node has a lat and lng in its dataset, then make the request
	 * immediately, otherwise, use the place-id in its dataset to get the lat and lng
	 * using Google's Geocoder class.
	 *
	 * @param {Node} prediction
	 */
	handlePredictionSelect = (prediction) => {
		if (prediction) {
			this.searchEl.value = prediction.textContent;
			this.searchEl.blur();

			// The picked prediction is now what's in the search bar, so it's what
			// the summary names.
			this.beginNewSearch(prediction.textContent);
			this.scrollToResultsOnNextSearch = true;

			if (prediction.dataset.lat && prediction.dataset.lng) {
				/**
				 * If the node has a lat and lng in its dataset, then make the request immediately.
				 */
				this.getPhysicians(prediction.dataset.lat, prediction.dataset.lng);
			} else if (prediction.dataset.placeId) {
				/**
				 * Otherwise, use the place-id in its dataset to get the lat and lng using Google's
				 * Geocoder class.
				 */
				this.geocoder.geocode(
					{ placeId: prediction.dataset.placeId },
					(results, status) => {
						if (status === 'OK') {
							if (results[0]) {
								const { location } = results[0].geometry;
								this.getPhysicians(location.lat(), location.lng());
							} else {
								console.log('No results found');
							}
						}
					},
				);
			}
		}
	};

	/**
	 * Open the appropriate share template based on the share method of the control clicked.
	 *
	 * @since 1.0.0
	 */
	handleShareControlClick = (e) => {
		const control = e.target.closest('.js-eml-share-control');
		const shareMethod = control.dataset.share;
		const perPage = this.locations.length;
		const shareUrl = `${this.settings.archiveUrl}?lat=${this.currentPosition.lat}&lng=${this.currentPosition.lng}&share=${shareMethod}&per_page=${perPage}${this.getDistanceParam()}`;

		window.open(shareUrl, '_blank');
	};

	/**
	 * Build the distance query string fragment for share and archive URLs, so the
	 * shared list matches the radius the user was actually looking at.
	 *
	 * The unit rides along with the number: a bare radius is meaningless without
	 * it, and a shared link should keep resolving to the same list even if the
	 * site's unit setting changes later.
	 *
	 * @since 1.2.0
	 *
	 * @return {String} Either an empty string or `&distance=N&units=U`.
	 */
	getDistanceParam = () => {
		if (!this.currentDistance) {
			return '';
		}

		const units = encodeURIComponent(this.settings.distanceUnit);

		return `&distance=${this.currentDistance}&units=${units}`;
	};

	/**
	 * The next radius option above the active one, or false if there isn't one.
	 *
	 * Drives the automatic widening in getPhysicians(). Because each step is
	 * strictly larger than the last, walking the list always terminates — at the
	 * largest option, where an empty result really is empty.
	 *
	 * @since 1.3.0
	 *
	 * @return {Number|Boolean} The next radius up, or false.
	 */
	getNextDistance = () => {
		// No radius means the search was already unbounded — there is no wider.
		if (!this.currentDistance) {
			return false;
		}

		const values = (this.settings.radiusOptions || [])
			.map((option) => parseFloat(option.value))
			.filter((value) => value > this.currentDistance)
			.sort((a, b) => a - b);

		return values.length ? values[0] : false;
	};

	/**
	 * Set the active radius and bring the radius selects along with it.
	 *
	 * Keeping the selects in step matters most when the widening below picks the
	 * radius rather than the user: the control has to show the radius the results
	 * are actually from.
	 *
	 * @since 1.3.0
	 *
	 * @param {Number|Boolean} distance The radius to apply.
	 */
	setDistance = (distance) => {
		this.currentDistance = distance;

		this.radiusEls.forEach((radiusEl) => {
			radiusEl.value = distance ? String(distance) : '';
		});
	};

	/**
	 * Start a fresh location search.
	 *
	 * Resets the radius to the one the user actually chose, so a previous search
	 * that had to widen itself to find anything doesn't quietly leave the next
	 * search running at that wider radius.
	 *
	 * @since 1.3.0
	 *
	 * @param {String} label What the results are "of", for the results summary.
	 */
	beginNewSearch = (label) => {
		this.searchLabel = label;
		this.widenedFrom = false;
		this.setDistance(this.preferredDistance);
	};

	handleLocationSearch = (event) => {
		// If event is provided, find the associated search input
		let searchInput = this.searchEl;
		if (event && event.target) {
			const searchBtn = event.target.closest('.eml-search-btn');
			if (searchBtn) {
				const container = searchBtn.closest('.eml-search__field-container');
				if (container) {
					const containerSearchInput =						container.querySelector('.js-eml-search');
					if (containerSearchInput) {
						searchInput = containerSearchInput;
					}
				}
			}
		}

		// Get the search value, checking both the current value and the stored last value
		const currentValue = searchInput.value.trim();
		const lastValue = searchInput.dataset.lastValue
			? searchInput.dataset.lastValue.trim()
			: '';
		const searchValue = currentValue || lastValue;

		// Don't proceed if search value is empty
		if (!searchValue) {
			this.setSearchAlert(
				this.getSearchAlert('Please enter a location to search.'),
			);
			return;
		}

		// Clear any existing error messages
		this.setSearchAlert();

		this.geocodeSearchValue(
			searchValue,
			(location) => {
				// Name the results after what was typed, not what Google matched.
				this.beginNewSearch(searchValue);
				this.scrollToResultsOnNextSearch = true;
				this.getPhysicians(location.lat(), location.lng());
			},
			() => {
				this.setSearchAlert(
					this.getSearchAlert('Please enter a valid location to search.'),
				);
			},
		);

		window.dataLayer.push({
			event: 'find_urologist_search',
			search_term: searchValue,
		});
	};

	/**
	 * Resolve what the user typed into a position.
	 *
	 * A bare postal code is ambiguous worldwide, and the geocoder would rather
	 * answer nothing than guess: "90210" comes back `ZERO_RESULTS` because it is
	 * also a postcode in Chile and in Mexico, while "90211" — which is only
	 * Beverly Hills — resolves fine (#12). Naming the string as a postal code
	 * instead of a free-form address is what lets Google rank those candidates
	 * rather than refuse to choose, so that is the retry.
	 *
	 * It only ever runs after the plain lookup has already failed, and a string
	 * that is no kind of postal code still comes back `ZERO_RESULTS` from it — so
	 * genuinely unresolvable input reaches `onFailure` exactly as before.
	 *
	 * @since 1.3.0
	 *
	 * @param {String}   searchValue What the user typed.
	 * @param {Function} onSuccess   Passed the resolved `google.maps.LatLng`.
	 * @param {Function} onFailure   Called when nothing resolves.
	 */
	geocodeSearchValue = (searchValue, onSuccess, onFailure) => {
		const countries = this.getCountryRestrictions();

		// Confine the lookup to the same countries the predictions are drawn from.
		// Without this the postal-code retry below accepts a postcode from anywhere
		// on earth: "3435" is nothing in the US, so the plain lookup fails as it
		// should, and then the retry resolves it in Victoria, Australia and searches
		// there. That reads as a working search that walks the radius up to its
		// widest and reports no providers, rather than as the bad input it is.
		//
		// This is a filter over the results rather than a `componentRestrictions`
		// on the request, because the geocoder — unlike the autocomplete — takes a
		// single country string, and passing it the configured array never fires the
		// callback at all. Not `ZERO_RESULTS`, not an error: the search just hangs.
		// Matching afterwards is also what lets more than one country be allowed.
		const handle = (onMiss) => (results, status) => {
			const matched = status === 'OK' && results
				? results.find((result) => this.isCountryAllowed(result, countries))
				: undefined;

			if (matched) {
				onSuccess(matched.geometry.location);
				return;
			}

			onMiss();
		};

		this.geocoder.geocode(
			{ address: searchValue },
			handle(() => {
				this.geocoder.geocode(
					{ componentRestrictions: { postalCode: searchValue } },
					handle(onFailure),
				);
			}),
		);
	};

	/**
	 * Whether a geocoder result sits in one of the configured countries.
	 *
	 * The configured list carries both the country names and their codes — "United
	 * States", "USA", "US" — so either half of Google's `country` component is
	 * enough to match, the same way the Salesforce sync matches a location's
	 * country against the setting.
	 *
	 * @since 1.2.2
	 *
	 * @param {Object}        result    A `google.maps.GeocoderResult`.
	 * @param {Array|Boolean} countries The configured countries, or false for any.
	 *
	 * @return {Boolean} True where the result is searchable.
	 */
	isCountryAllowed = (result, countries) => {
		if (!countries) {
			return true;
		}

		const country = (result.address_components || []).find(
			(component) => component.types.indexOf('country') !== -1,
		);

		if (!country) {
			return false;
		}

		return (
			countries.indexOf(country.long_name) !== -1
			|| countries.indexOf(country.short_name) !== -1
		);
	};

	/**
	 * The countries Google should confine a lookup to, or false where the site
	 * allows anywhere.
	 *
	 * Shared by the geocoder and the autocomplete so the two agree on what counts
	 * as a searchable place — a prediction the user can't then search, or a search
	 * that lands somewhere no prediction would have offered, is the mismatch this
	 * exists to prevent.
	 *
	 * @since 1.2.2
	 *
	 * @return {Array|Boolean} The configured countries, or false for no restriction.
	 */
	getCountryRestrictions = () => {
		const countries = this.settings.countryRestrictions || [];

		return countries.length && countries.indexOf('none') === -1
			? countries
			: false;
	};

	/**
	 * Update the autocomplete search predictions using google's AutocompleteService and the
	 * FATC autocomplete api endpoint.
	 *
	 * @since 1.0.0
	 */
	updateAutocompletePredictions = () => {
		/**
		 * Add classes to searchEl and predictionsEl.
		 */
		this.searchEl.classList.add('focused');
		this.predictionsEl.classList.add('active');

		const searchValue = this.searchEl.value;

		/**
		 * Only proceed if the searchValue, when trimmed, doesn't match the previous search term.
		 * Eliminates extra requests when the user types a space.
		 */
		if (searchValue.trim() !== this.currentSearchValue) {
			/**
			 * Only proceed if the searchValue isn't an empty string to eliminate extra requests.
			 */
			if (searchValue !== '') {
				/**
				 * First, get predictions from the FATC autocomplete API. Then pass the predictions along,
				 * and get additional predictions from the Google AutocompleteService. Finally, pass all
				 * predictions on and build out the dropdown HTML.
				 *
				 * @type {Promise}
				 */
				const locatorPredictionsPromise = fetch(
					encodeURI(`${this.settings.apiUrl}?search=${searchValue}&per_page=3`),
					this.fetchHeaders,
				).then((response) => response.json());

				const autocompleteOptions = {
					input: searchValue,
					types: ['(regions)'],
				};

				const countries = this.getCountryRestrictions();

				if (countries) {
					autocompleteOptions.componentRestrictions = {
						country: countries,
					};
				}

				const googlePredictionsPromise = new Promise((resolve) => {
					this.autocompleteService.getPlacePredictions(
						autocompleteOptions,
						(googlePredictions) => {
							resolve(googlePredictions || []);
						},
					);
				});

				Promise.all([locatorPredictionsPromise, googlePredictionsPromise])
					/**
					 * Merge Locator and Google predictions into one array and return it.
					 */
					.then((predictions) => predictions.reduce((acc, val) => acc.concat(val), []))
					.then((predictions) => {
						if (predictions.length > 0) {
							/**
							 * Placeholder for prediction dropdown HTML.
							 */
							let predictionsHtml = '';

							/**
							 * Loop through all predictions and add them to predictionsHtml.
							 */
							forEach(predictions, (i, prediction) => {
								let className = 'js-ac-prediction eml-dropdown-list__item';

								/**
								 * Add active class to the first prediction.
								 */
								if (i === 0) {
									className = `${className} active`;
								}

								if (prediction.lat && prediction.lng) {
									/**
									 * Handle locator predictions.
									 */
									predictionsHtml += `<li class="${className}" data-lat="${prediction.lat}" data-lng="${prediction.lng}">${prediction.post.post_title}</li>`;
								} else if (prediction.place_id) {
									/**
									 * Handle Google predictions.
									 */
									predictionsHtml += `<li class="${className}" data-place-id="${prediction.place_id}">${prediction.description}</li>`;
								}
							});

							/**
							 * Update the prediction dropdown's HTML.
							 */
							this.predictionsEl.innerHTML = predictionsHtml;
						} else {
							/**
							 * Add a helpful message if no results are found.
							 */
							this.predictionsEl.innerHTML =								'<div id="js-eml-no-results-item" class="eml-dropdown-list__item">No results found</div>';
						}
					})
					.catch(() => {
						/**
						 * Add a helpful message if no results are found.
						 */
						this.predictionsEl.innerHTML =							'<div id="js-eml-no-results-item" class="eml-dropdown-list__item">No results found</div>';
					});
			}

			/**
			 * Keep track of the new search value for later comparison.
			 */
			this.currentSearchValue = searchValue.trim();
		}
	};

	/**
	 * Show a message in the search alert boxes, or clear them.
	 *
	 * Goes to every box rather than one, so the message lands on whichever search
	 * form is on screen — see `searchAlertEls` for why there are two.
	 *
	 * @since 1.3.0
	 *
	 * @param {String} html Markup for the message. Empty clears the boxes.
	 */
	setSearchAlert = (html = '') => {
		this.searchAlertEls.forEach((alertEl) => {
			alertEl.innerHTML = html;
		});
	};

	/**
	 * Build the markup for a search alert.
	 *
	 * @since 1.3.0
	 *
	 * @param {String} message The message to show.
	 * @return {String} The alert markup.
	 */
	getSearchAlert = (message) => `<div class="alert alert--danger alert--inline">
			<span class="alert__content">${message}</span>
		</div>`;

	/**
	 * Ask for a location before a distance-based control can do anything.
	 *
	 * Radius and sort are both measured from an origin, so on the unfiltered list
	 * the locator loads with when geolocation is blocked they had nothing to act
	 * on and appeared to do nothing at all. Say what's missing and put the cursor
	 * where the user has to fix it rather than failing silently.
	 *
	 * @since 1.3.0
	 *
	 * @param {Event} event The control's event, used to find the search input in
	 *                      the same form — the locator renders two, and the one
	 *                      holding the control is by definition the visible one.
	 */
	promptForSearchLocation = (event) => {
		this.setSearchAlert(
			this.getSearchAlert('Please enter a location to search.'),
		);

		const container = event.target.closest('.eml-search__field-container');
		const searchEl = container
			? container.querySelector('.js-eml-search')
			: this.searchEl;

		if (!searchEl) {
			return;
		}

		searchEl.focus();

		// A geolocation message or an earlier term would otherwise be typed into
		// rather than replaced.
		searchEl.select();
	};

	/**
	 * Recenter the map on the current center.
	 *
	 * @since 1.0.0
	 */
	recenterMap = () => {
		this.map.setCenter(this.mapCenter);
	};

	/**
	 * Make a request to the WP REST API endpoint using lat and lng to return the closest locations.
	 *
	 * @since 1.0.0
	 *
	 * @param {Number} lat Latitude
	 * @param {Number} lng Longitude
	 * @param {Number} limit Number of results to return with the request.
	 */
	getPhysicians = (lat = false, lng = false, page = 1, distance = null) => {
		// An explicit distance wins; otherwise fall back to the selected radius so
		// every caller stays consistent without having to pass it through.
		const activeDistance = distance === null ? this.currentDistance : distance;

		// Claim the latest request. Anything already in flight is now stale and
		// stops being allowed to write state — see `requestId`.
		this.requestId += 1;
		const { requestId } = this;

		let apiUrl = this.settings.physiciansApiUrl;
		if (lat || lng || page) {
			apiUrl += '?';
			const params = [];

			if (lat) {
				params.push(`lat=${lat}`);
			}

			if (lng) {
				params.push(`lng=${lng}`);
			}

			if (page) {
				params.push(`page=${page}`);
			}

			if (activeDistance) {
				params.push(`distance=${activeDistance}`);
				params.push(`units=${this.settings.distanceUnit}`);
			}

			// Only sent when non-default, so the request is unchanged from before
			// this feature existed while "Nearest" is selected.
			if (this.currentSort && this.currentSort !== 'distance') {
				params.push(`sort=${this.currentSort}`);
			}

			if (this.locatorEl.dataset.landing === 'true') {
				params.push('landing=true');
			}

			if (params.length > 0) {
				apiUrl += params.join('&');
			}
		}

		const emlPreFetchLocations = new CustomEvent('emlPreFetchLocations', {
			detail: { apiUrl },
		});

		window.dispatchEvent(emlPreFetchLocations);

		return fetch(emlPreFetchLocations.detail.apiUrl, this.fetchHeaders)
			.then((response) => {
				// Superseded while in flight: the totals and the page number below
				// belong to a search the user has already moved on from.
				if (requestId !== this.requestId) {
					return null;
				}

				this.pagination.totalPosts = parseInt(
					response.headers.get('X-WP-Total'),
				);
				this.pagination.totalPages = parseInt(
					response.headers.get('X-WP-TotalPages'),
				);
				this.pagination.currentPage = page;

				forEach(this.paginationEls, (i, paginationEl) => {
					if (page < this.pagination.totalPages) {
						paginationEl.style.display = 'block';
					} else {
						paginationEl.style.display = 'none';
					}
				});
				return response.json();
			})
			.then((physicians) => {
				// Either this request was superseded before its headers were read,
				// or it was superseded while the body was being parsed. Its results
				// must not be concatenated onto the current list.
				if (physicians === null || requestId !== this.requestId) {
					return null;
				}

				/**
				 * Nothing within this radius? Step up to the next option and try
				 * again before rendering, so a remote search lands on the closest
				 * radius that actually has providers instead of an empty list.
				 *
				 * Returning the retry keeps it in the same promise chain, so callers
				 * still resolve once — after the attempt that finally renders.
				 */
				const widerDistance = this.getNextDistance();

				if (
					page === 1
					&& lat
					&& lng
					&& this.pagination.totalPosts === 0
					&& widerDistance
				) {
					// Keep the first radius that failed, not the last, so the notice
					// names the radius the user actually asked for.
					if (!this.widenedFrom) {
						this.widenedFrom = this.currentDistance;
					}

					this.setDistance(widerDistance);
					return this.getPhysicians(lat, lng, 1);
				}

				this.locations =					page === 1
					? physicians
					: this.dedupePhysicians(this.locations.concat(physicians));
				this.currentPosition = { lat, lng };
				this.setSearchAlert();

				const emlPostFetchLocations = new CustomEvent('emlPostFetchLocations', {
					detail: {
						locations: this.locations,
						lat,
						lng,
						pagination: this.pagination,
					},
				});

				window.dispatchEvent(emlPostFetchLocations);

				this.handleNewLocations();
				this.saveSearchState();

				if (this.emailEnabled) {
					this.emailForm.action = `${this.settings.archiveUrl}?lat=${lat}&lng=${lng}${this.getDistanceParam()}`;
				}

				forEach(this.loadMoreButtons, (i, loadMoreButton) => {
					loadMoreButton.removeAttribute('disabled');
				});

				return null;
			});
	};

	/**
	 * Drop any physician already in the list.
	 *
	 * A safety net under "Load more": the request guard in getPhysicians() keeps
	 * pages from two different result sets out of the same list, but a page can
	 * still overlap the one before it whenever the underlying set shifts between
	 * requests — a Salesforce sync landing mid-search, or a tie in the sort order
	 * that MySQL breaks differently from one query to the next. Either way the
	 * same card must not appear twice.
	 *
	 * @since 1.2.3
	 *
	 * @param {Array} physicians The accumulated results.
	 * @return {Array} The results, first occurrence of each ID kept in place.
	 */
	dedupePhysicians = (physicians) => {
		const seen = [];

		return physicians.filter((physician) => {
			// Anything without an ID can't be matched, so it is left alone rather
			// than collapsed into one entry.
			if (!physician || !physician.ID) {
				return true;
			}

			if (seen.indexOf(physician.ID) !== -1) {
				return false;
			}

			seen.push(physician.ID);

			return true;
		});
	};

	/**
	 * Load more locations, if available, when the load more button is clicked.
	 */
	handleLoadMore = () => {
		if (this.pagination.currentPage < this.pagination.totalPages) {
			forEach(this.loadMoreButtons, (i, loadMoreButton) => {
				loadMoreButton.setAttribute('disabled', 'disabled');
			});

			// Store the current scroll position
			const currentScroll = window.pageYOffset;

			this.getPhysicians(
				this.currentPosition.lat,
				this.currentPosition.lng,
				this.pagination.currentPage + 1,
			).then(() => {
				// Wait for DOM to update
				requestAnimationFrame(() => {
					// Kill existing ScrollTrigger
					if (this.scrollTrigger) {
						this.scrollTrigger.kill();
					}

					// Create new ScrollTrigger, if this build provides one.
					if (typeof this.initScrollTrigger === 'function') {
						this.initScrollTrigger();
					}

					// Restore scroll position
					window.scrollTo(0, currentScroll);
				});
			});
		}
	};

	/**
	 * Open the email form when an email form control is clicked,
	 * and update the email form's "action" attribute in the process.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	handleEmailFormControlClick = (e) => {
		const control = e.target.closest('.js-eml-email-control');
		if (control) {
			this.locatorEl.classList.add('show-email-form');

			/**
			 * If this isn't a control inside of a location, reset the email form action
			 * back to the archive url to share the full list of locations.
			 */
			const location = control.closest('.eml-location');
			if (!location) {
				const { lat, lng } = this.currentPosition;
				this.emailForm.action = `${this.settings.archiveUrl}?lat=${lat}&lng=${lng}`;
			}
		}
	};

	/**
	 * Close the email form when clicking outside of it or hitting the escape key.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	handleEmailFormClose = (e) => {
		const { target } = e;
		if (
			this.emailFormContainer === target
			|| this.emailFormCloseButton === target
		) {
			this.closeEmailForm();
		}
	};

	/**
	 * Close the email form and reset any alerts.
	 *
	 * @since 1.0,0
	 */
	closeEmailForm = () => {
		this.locatorEl.classList.remove('show-email-form');
		this.resetEmailFormAlert();
	};

	/**
	 * Clear any alerts triggered by the email form.
	 *
	 * @since 1.0.0
	 */
	resetEmailFormAlert = () => {
		this.emailFormAlert.classList.remove('success');
		this.emailFormAlert.classList.remove('danger');
		this.emailFormAlert.innerHTML = '';
	};

	/**
	 * Function to run when the email form successfully submits.
	 *
	 * @since 1.0.0
	 */
	handleEmailFormSuccess = () => {
		this.emailFormAlert.classList.remove('loading');
		this.emailFormAlert.innerHTML = 'Email sent!';
	};

	/**
	 * Function to run when the email form fails to submit.
	 *
	 * @since 1.0.0
	 */
	handleEmailFormError = () => {
		this.emailFormAlert.classList.remove('success');
		this.emailFormAlert.classList.remove('loading');
		this.emailFormAlert.classList.add('danger');
		this.emailFormAlert.innerHTML = 'Email failed to send. Please try again.';
	};

	/**
	 * Use ajax to submit the form, which will trigger wp_mail() to send the email.
	 *
	 * @since 1.0.0
	 *
	 * @param {Event} e
	 */
	handleEmailFormSubmit = (e) => {
		e.preventDefault();

		/**
		 * Reset any previous alerts triggered by the email form,
		 * and show loading animation.
		 */
		this.resetEmailFormAlert();
		this.emailFormAlert.classList.add('success');
		this.emailFormAlert.classList.add('loading');
		const startTime = new Date().getTime() / 1000;

		/**
		 * Build the url to open that will trigger wp_mail().
		 */
		const data = serialize(this.emailForm);
		let url = this.emailForm.action;
		url += this.emailForm.action.indexOf('?') > -1 ? '&' : '?';
		url += data;

		fetch(url, this.fetchHeaders)
			.then((response) => response.json())
			.then((json) => {
				if (json.sent) {
					const endTime = new Date().getTime() / 1000;
					const requestDuration = endTime - startTime;

					/**
					 * Check how long the request took, and if it was less than 2 seconds,
					 * add a delay so there is some percieved delay and it looks like something
					 * happened.
					 */
					if (requestDuration < 2) {
						setTimeout(() => {
							this.handleEmailFormSuccess();
						}, 2000);
					} else {
						this.handleEmailFormSuccess();
					}
				} else {
					this.handleEmailFormError();
				}
			});
	};

	/**
	 * Add location details HTML to the mapLocationDetailsEl, slide map into view and center on pin.
	 *
	 * @since 1.0.0
	 *
	 * @param {Number} index The index of the location in this.locations
	 */
	showMapLocationDetails = (index) => {
		/**
		 * Use the passed in index value to get the location, marker, and infowindow.
		 */
		const location = this.locations[index];
		const marker = this.markers[index];
		const infowindow = this.infowindows[index];

		// Safety check
		if (!location || !marker || !infowindow) {
			console.warn('Location, marker, or infowindow not found at index', index);
			return;
		}

		/**
		 * Update the email form action, if email sharing is enabled.
		 */
		if (this.emailEnabled) {
			this.emailForm.action = location.permalink;
		}

		/**
		 * Close any open infowindows.
		 */
		if (this.hoverInfowindow) {
			this.hoverInfowindow.close();
			this.hoverInfowindow = null;
		}
		if (this.activeInfowindow) {
			this.activeInfowindow.close();
		}

		/**
		 * Set marker to hover state to show it's selected
		 */
		marker.setIcon(this.markerIconHover);

		/**
		 * Open the target infowindow.
		 *
		 * `shouldFocus: false` because Google otherwise moves focus into the bubble
		 * as it opens. That focus paints the bubble's own ring for as long as the
		 * browser still reads the visit as keyboard-driven — which it does until the
		 * first click — so the border showed on a fresh page and vanished after any
		 * link or filter was touched (#54). Nothing here is keyboard intent, so
		 * nothing should take focus.
		 */
		infowindow.open({ map: this.map, anchor: marker, shouldFocus: false });

		/**
		 * Set the target infowindow as the activeInfowindow.
		 */
		this.activeInfowindow = infowindow;

		const markerPosition = marker.getPosition();
		const currentZoom = this.map.getZoom();

		// Ahead of the pan, because changing the zoom moves the centre and would
		// undo it.
		if (currentZoom < 12) {
			this.map.setZoom(12);
		}

		// On a landing page the bubble is left at its full height (see
		// _infowindow.scss), so how much room it needs is only knowable once it
		// exists — and `domready` is where it does. Waiting for it keeps this to the
		// one pan it always was, rather than a centring pan followed by a visible
		// correction.
		if (this.isLandingPageTemplate()) {
			this.google.maps.event.addListenerOnce(infowindow, 'domready', () => {
				this.centreOnMarker(markerPosition, this.getInfowindowReach(index));
			});

			return;
		}

		this.centreOnMarker(markerPosition);
	};

	/**
	 * How far an open bubble and its pin reach either side of the point the map
	 * centres on.
	 *
	 * That point is the pin's anchor, which is neither the top nor the bottom of
	 * what the reader sees: the bubble stacks above it — the pin's own height down
	 * to the anchor, the tail, then the bubble — and the pin's tip hangs below it.
	 * Centring the bubble alone therefore left the pair sitting a good 65px high in
	 * the map. Both halves are wanted, and the difference between them is the drop
	 * that centres the pair.
	 *
	 * @since 1.3.0
	 *
	 * @param {Number} index The infowindow's index, to find it in the DOM.
	 *
	 * @return {Object} `{above, below}` in pixels.
	 */
	getInfowindowReach = (index) => {
		const content = this.locatorEl.querySelector(
			`[data-eml-infowindow="${index}"]`,
		);
		const bubble = content ? content.closest('.gm-style-iw-c') : null;
		const bubbleHeight = bubble
			? bubble.getBoundingClientRect().height
			: EML_INFOWINDOW_HEIGHT;

		// The pin's size and anchor, as `updateMarkerIconSizes` set them for this
		// breakpoint. The anchor sits inside the icon, so it splits the pin's height
		// between the two directions.
		const icon = this.markerIconHover || {};
		const iconHeight = icon.scaledSize ? icon.scaledSize.height : 0;
		const anchorY = icon.anchor ? icon.anchor.y : iconHeight;

		return {
			above: bubbleHeight + EML_INFOWINDOW_TAIL + anchorY,
			below: Math.max(iconHeight - anchorY, 0),
		};
	};

	/**
	 * Bring a marker and its open infowindow into the middle of the map.
	 *
	 * The pin used to be centred only when it was off screen or close to an edge,
	 * worked out from the projection by hand. That left the common case — a pin
	 * already comfortably in view — untouched, and the bubble opens *upward*, so a
	 * pin anywhere in the top half of the map came up with its bubble clipped or
	 * hidden behind the site header (#18).
	 *
	 * So: always pan, and leave the pin below the centre by half the bubble's height
	 * rather than on it, which is what centres pin and bubble together. Two things
	 * bound the offset — the depth the module crops off the map's top edge (see
	 * `fitMapBounds`), which is not usable space, and a quarter of what is left, so a
	 * short map isn't pushed so far that the pin slides off the bottom.
	 *
	 * Landing pages take the top branch instead. `getMapCroppedDepth()` reads the
	 * gap between the map and its container, which there is the whole lift that puts
	 * the sticky map under the header — some ten thousand pixels, not a crop — so
	 * half of it as a pan sent the map, pin and bubble clean off into the ocean. The
	 * slice that is really on screen answers the same question honestly.
	 *
	 * @since 1.3.0
	 *
	 * @param {google.maps.LatLng} markerPosition Position to centre on.
	 * @param {Object}             reach          `{above, below}` from
	 *                                            `getInfowindowReach()`, measured
	 *                                            where the bubble is already up.
	 */
	centreOnMarker = (markerPosition, reach = null) => {
		this.map.panTo(markerPosition);

		if (this.isLandingPageTemplate()) {
			const mapEl = this.locatorEl.querySelector('.js-eml-map');
			const visible = this.getMapVisibleRect();

			if (!mapEl || !visible) {
				return;
			}

			const { above, below } = reach || this.getInfowindowReach();
			const mapRect = mapEl.getBoundingClientRect();
			// `panTo` leaves the anchor on the middle of the whole map box, which on a
			// lifted map is above the screen; this is how far down to the middle of
			// what the reader can see.
			const toVisibleCentre =				(visible.top + visible.bottom) / 2
				- (mapRect.top + mapRect.bottom) / 2;
			// And then far enough that the bubble above the anchor and the pin's tip
			// below it straddle that centre, plus the bias that keeps the bubble off
			// the crowded top edge.
			const half = (visible.bottom - visible.top) / 2;
			const wanted =				(above - below) / 2 + half * 2 * EML_INFOWINDOW_BIAS;

			// Bounds, so the bias can never push either end out of the slice: as low
			// as the pin's tip allows, as high as the bubble's top does. Where the
			// pair is taller than the slice the two cross, and the lower bound wins —
			// a pin below the fold reads as broken in a way a trimmed bubble doesn't.
			const lowest = half - EML_INFOWINDOW_MARGIN - below;
			const highest = above - half + EML_INFOWINDOW_MARGIN;
			const drop = Math.max(
				Math.min(Math.max(wanted, Math.min(highest, lowest)), lowest),
				0,
			);
			const offset = Math.round(toVisibleCentre + drop);

			// A negative y moves the centre north, which moves the pin *down* the screen.
			if (offset) {
				this.map.panBy(0, -offset);
			}

			return;
		}

		const cropped = this.getMapCroppedDepth();
		const visibleHeight = Math.max(this.map.getDiv().offsetHeight - cropped, 0);

		// Half the crop centres the pin in the visible slice rather than in the full
		// box; the rest drops it far enough for the bubble to clear the top.
		const offset =			cropped / 2
			+ Math.min(EML_INFOWINDOW_HEIGHT / 2, visibleHeight / 4);

		// A negative y moves the centre north, which moves the pin *down* the screen.
		if (offset > 0) {
			this.map.panBy(0, -offset);
		}
	};

	/**
	 * Remove class that slides location details out of view.
	 *
	 * @since 1.0.0
	 */
	hideMapLocationDetails = () => {
		this.fitMapBounds(this.map.bounds);
		this.mapContainerEl.classList.remove('has-details');
		this.activeInfowindow.close();

		if (this.markers) {
			forEach(this.markers, (i, marker) => {
				marker.setIcon(this.markerIcon);
			});
		}

		/**
		 * Scroll the mapLocationDetailsEl to the top so it's posistioned properly when the next location
		 * is opened. Doing this on a timeout to avoid a flicker where the element jumps to
		 * the top before the slide transition can complete.
		 */
		setTimeout(() => {
			this.mapLocationDetailsEl.scrollTop = 0;
		}, 300);
	};

	/**
	 * Remove the current locations from the results list and their markers from the map.
	 *
	 * @since 1.0.0
	 */
	resetMap = () => {
		if (this.markers) {
			forEach(this.markers, (i, marker) => {
				marker.setMap(null);
				marker.setIcon(this.markerIcon);
			});

			this.markers = [];
		}

		/**
		 * Remove any added classes from the map container.
		 */
		this.mapContainerEl.classList.remove('has-details');
	};

	handleMapLocationClick = (e) => {
		if (e.target.classList.contains('eml-location__infolink')) {
			e.stopPropagation();
			const targetEl = e.target.closest('.eml-location');
			const locationEls = this.mapLocationsEl.querySelectorAll('.eml-location');

			forEach(locationEls, (i, locationEl) => {
				if (locationEl === targetEl) {
					this.markers[i].setIcon(this.markerIconHover);
				}
			});
		}
	};

	handleCardTouch = (e) => {
		// Any link in the card — the CTAs, but also the phone and Book Online links
		// inside the body — takes over the tap. Otherwise the card's own handling
		// below (pin popup, map pan) changes the page between touchend and the
		// click, and iOS Safari drops the click, so the link only works on a long
		// press. `click()` rather than setting `location.href` so `target="_blank"`
		// is kept, and `tel:` still opens because it runs inside the tap.
		const link = e.target.closest('a[href]');
		if (link) {
			e.preventDefault();
			link.click();
			return;
		}

		const body = e.target.closest('.eml-physician-card__body');
		if (body) {
			const card = body.closest('.eml-location');
			const locationEls = this.mapLocationsEl.querySelectorAll('.eml-location');
			forEach(locationEls, (i, locationEl) => {
				if (locationEl === card) {
					this.markers[i].setIcon(this.markerIconHover);
					this.showMapLocationDetails(i);
				}
			});
		}
	};

	openHoverInfowindow = (index) => {
		if (this.infowindows[index] === this.activeInfowindow) return;
		if (
			this.hoverInfowindow
			&& this.hoverInfowindow !== this.infowindows[index]
		) {
			this.hoverInfowindow.close();
		}
		this.hoverInfowindow = this.infowindows[index];

		// Hovering is never keyboard intent, so the bubble must not take focus and
		// paint its focus ring — see showMapLocationDetails() (#54).
		this.hoverInfowindow.open({
			map: this.map,
			anchor: this.markers[index],
			shouldFocus: false,
		});
		this.panInfowindowIntoView(this.hoverInfowindow, index);
	};

	/**
	 * The part of the map that is actually on screen, in viewport coordinates.
	 *
	 * Two things take a bite out of the top edge: the module crops whatever the
	 * map's lift pushes above itself (see `fitMapBounds`), and the search band
	 * floats over what's left. Both shrink to nothing as the page scrolls, so this
	 * is measured per call.
	 *
	 * Landing pages clip against the window and the sticky header rather than
	 * against the module. The lift there runs to some ten thousand pixels, which
	 * leaves the map entirely outside its own container's box, and clipping to the
	 * container returned a slice of no height at all — with nothing on screen to
	 * pan a bubble into, and no way to tell a bubble that had gone off the map from
	 * one sitting comfortably in the middle of it.
	 *
	 * @since 1.3.0
	 *
	 * @return {Object|null} `{top, bottom, left, right}`, or null with no map.
	 */
	getMapVisibleRect = () => {
		const mapContainer = this.locatorEl.querySelector('.js-eml-map-container');
		const mapEl = this.locatorEl.querySelector('.js-eml-map');

		if (!mapContainer || !mapEl) {
			return null;
		}

		const mapRect = mapEl.getBoundingClientRect();
		const searchEl = this.locatorEl.querySelector('.eml-search');
		const isLanding = this.isLandingPageTemplate();

		let top;
		let bottom;
		// The map runs the full width of the module, so on a landing page — where
		// the container's box is no guide to where the map is — its own edges are
		// the horizontal bounds.
		let { left, right } = mapRect;

		if (isLanding) {
			const siteHeader = document.querySelector('.site-header');
			const headerBottom = siteHeader
				? siteHeader.getBoundingClientRect().bottom
				: 0;

			top = Math.max(mapRect.top, headerBottom);
			bottom = Math.min(mapRect.bottom, document.documentElement.clientHeight);

			/**
			 * Then whatever the search band puts over the map's top edge. Every bar
			 * is measured rather than the first one found: there are two bands, the
			 * desktop one and the mobile one inside the map container, and which is
			 * the live one depends on the breakpoint — so a plain `querySelector()`
			 * was reading the hidden desktop band's zero-height box on a phone and
			 * missing the control row that really was over the map. The rows are
			 * counted alongside their bands because negative margins let a row hang
			 * below the band it sits in.
			 */
			const bars = this.locatorEl.querySelectorAll(
				'.eml-search, .eml-search__field-container',
			);

			forEach(bars, (i, bar) => {
				const barRect = bar.getBoundingClientRect();

				// Nothing to answer for if it's hidden, off to the side of the map
				// rather than over it, or not reaching into the slice at all.
				if (
					barRect.height <= 0
					|| barRect.left >= mapRect.right
					|| barRect.right <= mapRect.left
					|| barRect.bottom <= top
					|| barRect.top >= bottom
				) {
					return;
				}

				top = Math.min(barRect.bottom, bottom);
			});
		} else {
			const containerRect = mapContainer.getBoundingClientRect();

			top = Math.max(mapRect.top, containerRect.top);
			bottom = Math.min(mapRect.bottom, containerRect.bottom);
			left = Math.max(mapRect.left, containerRect.left);
			right = Math.min(mapRect.right, containerRect.right);
		}

		// Landing pages have taken every bar into account above.
		if (!isLanding && searchEl) {
			const searchRect = searchEl.getBoundingClientRect();

			// Only counts where the band is over the map — below `md` the desktop band
			// is off to the side of it rather than on top.
			const overlaps =				searchRect.left < mapRect.right && searchRect.right > mapRect.left;

			if (overlaps && searchRect.bottom > top) {
				top = Math.min(searchRect.bottom, mapRect.bottom);
			}
		}

		return {
			top,
			bottom,
			left,
			right,
		};
	};

	/**
	 * Pan an open infowindow into the visible slice of the map.
	 *
	 * Replaces the auto-pan turned off in `openHoverInfowindow`, so it has all four
	 * edges to answer for, not just the covered top one. The bubble only exists in
	 * the DOM from `domready`, and with auto-pan off nothing else is moving the map
	 * by then, so what it measures there is where the bubble stays.
	 *
	 * @since 1.3.0
	 *
	 * @param {google.maps.InfoWindow} infowindow The infowindow that was opened.
	 * @param {Number}                 index      Its index, to find it in the DOM.
	 */
	panInfowindowIntoView = (infowindow, index) => {
		this.google.maps.event.addListenerOnce(infowindow, 'domready', () => {
			const visible = this.getMapVisibleRect();
			const content = this.locatorEl.querySelector(
				`[data-eml-infowindow="${index}"]`,
			);
			const bubble = content ? content.closest('.gm-style-iw-c') : null;

			if (!visible || !bubble) {
				return;
			}

			const rect = bubble.getBoundingClientRect();

			// A positive delta moves the map's centre that way, which moves the bubble
			// the opposite way — so each is the overshoot, signed to undo itself.
			let x = 0;
			let y = 0;

			if (rect.top < visible.top + EML_INFOWINDOW_MARGIN) {
				y = rect.top - visible.top - EML_INFOWINDOW_MARGIN;
			} else if (rect.bottom > visible.bottom - EML_INFOWINDOW_MARGIN) {
				y = rect.bottom - visible.bottom + EML_INFOWINDOW_MARGIN;
			}

			if (rect.left < visible.left + EML_INFOWINDOW_MARGIN) {
				x = rect.left - visible.left - EML_INFOWINDOW_MARGIN;
			} else if (rect.right > visible.right - EML_INFOWINDOW_MARGIN) {
				x = rect.right - visible.right + EML_INFOWINDOW_MARGIN;
			}

			if (x || y) {
				this.map.panBy(x, y);
			}
		});
	};

	scheduleHoverClose = () => {
		this.hoverCloseTimeout = setTimeout(() => {
			if (
				this.hoverInfowindow
				&& this.hoverInfowindow !== this.activeInfowindow
			) {
				this.hoverInfowindow.close();
				this.hoverInfowindow = null;
			}
		}, 150);
	};

	cancelHoverClose = () => {
		clearTimeout(this.hoverCloseTimeout);
		this.hoverCloseTimeout = null;
	};

	handleMapLocationHover = (e) => {
		e.stopPropagation();
		const targetEl = e.target.closest('.eml-location');
		const locationEls = this.mapLocationsEl.querySelectorAll('.eml-location');

		forEach(locationEls, (i, locationEl) => {
			if (locationEl === targetEl) {
				this.cancelHoverClose();
				this.markers[i].setIcon(this.markerIconHover);
				this.openHoverInfowindow(i);
			}
		});
	};

	handleMapLocationLeave = (e) => {
		e.stopPropagation();
		const targetEl = e.target.closest('.eml-location');
		const locationEls = this.mapLocationsEl.querySelectorAll('.eml-location');

		forEach(locationEls, (i, locationEl) => {
			if (locationEl === targetEl) {
				this.markers[i].setIcon(this.markerIcon);
				if (
					this.hoverInfowindow
					&& this.hoverInfowindow !== this.activeInfowindow
				) {
					this.scheduleHoverClose();
				}
			}
		});
	};

	/**
	 * Get the HTML markup for the no results alert message.
	 *
	 * @since 1.1.0
	 */
	getNoResultsAlert = () => `<li class="eml-no-results">
		<div class="eml-alert danger">${this.settings.noResultsMessage}</div>
	</li>`;

	hideClusterLocationDetails = (clusterBounds) => {
		// Close any active infowindow
		if (this.activeInfowindow) {
			this.activeInfowindow.close();
			this.activeInfowindow = null;
		}

		// Reset all markers to normal icon
		this.markers.forEach((marker) => {
			if (marker) {
				marker.setIcon(this.markerIcon);
			}
		});

		// Fit to cluster bounds and center
		this.fitMapBounds(clusterBounds);
		this.map.setCenter(clusterBounds.getCenter());
	};

	handleClusterLoadMore = () => {
		this.mapLocationsEl.innerHTML = '';
		let html = '';

		forEach(this.locations, (i, location) => {
			html += location.map_item_html;
		});

		this.mapLocationsEl.innerHTML = html;
		this.handleLocationsRendered();

		forEach(this.loadMoreButtons, (i, loadMoreButton) => {
			loadMoreButton.removeEventListener('click', this.handleClusterLoadMore);
			loadMoreButton.classList.remove('eml-load-cluster-locations');
			loadMoreButton.addEventListener('click', this.handleLoadMore);
			loadMoreButton.innerHTML = 'Load more';
		});

		this.map.setCenter(this.map.bounds.getCenter());
		this.fitMapBounds(this.map.bounds);
	};

	showClusterDetails = (cluster) => {
		const clusterBounds = cluster.bounds;
		const isMobile = window.innerWidth < 1025; // More standard mobile breakpoint
		const markerCount = cluster.markers.length;

		// First, fit the map to the bounds and center it
		this.fitMapBounds(clusterBounds);
		this.map.setCenter(clusterBounds.getCenter());

		// Adaptive zoom based on cluster size
		if (markerCount >= 10) {
			// Very large clusters (10+)
			const zoomLevel = isMobile
				? Math.min(this.map.getZoom() + 1, 14)
				: Math.min(this.map.getZoom() + 1, 13);
			this.map.setZoom(zoomLevel);
		} else if (markerCount >= 6) {
			// Medium clusters (6-9)
			const zoomLevel = isMobile
				? Math.min(this.map.getZoom() + 1, 15)
				: Math.min(this.map.getZoom() + 1, 14);
			this.map.setZoom(zoomLevel);
		} else {
			// Small clusters (2-5)
			const zoomLevel = isMobile
				? Math.min(this.map.getZoom() + 2, 16)
				: Math.min(this.map.getZoom() + 2, 15);
			this.map.setZoom(zoomLevel);
		}

		// Now update the map locations list with the markers from this cluster
		this.mapLocationsEl.innerHTML = '';
		let html = '';

		forEach(cluster.markers, (i, marker) => {
			const markerIndex = this.markers.indexOf(marker);
			if (markerIndex !== -1 && this.locations[markerIndex]) {
				html += this.locations[markerIndex].map_item_html;

				// Clear any existing cluster-specific click listeners on this marker
				this.google.maps.event.clearListeners(marker, 'click');

				// Add the original marker click event (for location details)
				this.google.maps.event.addListener(marker, 'click', () => {
					this.showMapLocationDetails(markerIndex);
				});
			}
		});

		this.mapLocationsEl.innerHTML = html;
		this.handleLocationsRendered();

		// Setup the load more buttons for this cluster area
		forEach(this.loadMoreButtons, (i, loadMoreButton) => {
			loadMoreButton.removeEventListener('click', this.handleLoadMore);
			loadMoreButton.classList.add('eml-load-cluster-locations');
			loadMoreButton.addEventListener('click', this.handleClusterLoadMore);
			loadMoreButton.innerHTML = 'Load more in this area';
		});
	};

	updateMarkerIconSizes = () => {
		const isMobile = window.matchMedia('(max-width: 768px)').matches;
		const markerW = isMobile ? 60 : 120;
		const markerH = isMobile ? 68 : 136;
		const anchorX = isMobile ? 30 : 60;
		const anchorY = isMobile ? 48 : 95;

		const scaledSize = new this.google.maps.Size(markerW, markerH);
		const anchor = new this.google.maps.Point(anchorX, anchorY);

		this.markerIcon = { url: this.markerIcon.url, scaledSize, anchor };
		this.markerIconHover = {
			url: this.markerIconHover.url,
			scaledSize,
			anchor,
		};
	};

	updateMapLocations = () => {
		/**
		 * Clear map markers and map classes before anything else.
		 */
		this.updateMarkerIconSizes();
		this.resetMap();

		/**
		 * Placeholder for storing html.
		 */
		let html = '';

		if (this.locations.length > 0) {
			/**
			 * Reset existing map bounds with new LatLngBounds object.
			 */
			this.map.bounds = new this.google.maps.LatLngBounds();

			forEach(this.locations, (i, location) => {
				/**
				 * Add marker for location. Physicians carry their location's lat/lng.
				 */
				this.markers[i] = new this.google.maps.Marker({
					position: new this.google.maps.LatLng(location.lat, location.lng),
					map: this.map,
					icon: this.markerIcon,
				});

				/**
				 * Extend the bounds to include the marker.
				 */
				this.map.bounds.extend(this.markers[i].getPosition());

				// The index is carried into the bubble's own markup because that is the
				// only handle on it: an InfoWindow doesn't expose the node Google builds
				// for it, and a click-opened bubble can still be up when a hover opens a
				// second one — so `panInfowindowIntoView` can't just take the first
				// `.gm-style-iw-c` it finds.
				let infoWindowContent = `<div class="eml-infowindow" data-eml-infowindow="${i}">`;

				infoWindowContent += '<header><address>';

				if (location.location_name) {
					infoWindowContent += `<span class="eml-infowindow__title">${location.location_name}</span>`;
				}

				if (location.location_formatted_address) {
					infoWindowContent += `<span class="eml-infowindow__address">${location.location_formatted_address}</span>`;
					const encodedAddress = encodeURIComponent(
						location.location_formatted_address,
					);
					const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
					const arrowIcon =						'<svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none" aria-hidden="true"><path d="M4.5 9.75H6V7.5H8.625V9.375L11.25 6.75L8.625 4.125V6H5.25C5.0375 6 4.85938 6.07188 4.71563 6.21563C4.57188 6.35938 4.5 6.5375 4.5 6.75V9.75ZM7.5 15C7.3125 15 7.12813 14.9625 6.94688 14.8875C6.76563 14.8125 6.6 14.7 6.45 14.55L0.45 8.55C0.3 8.4 0.1875 8.23438 0.1125 8.05313C0.0375 7.87188 0 7.6875 0 7.5C0 7.3125 0.0375 7.12813 0.1125 6.94688C0.1875 6.76563 0.3 6.6 0.45 6.45L6.45 0.45C6.6 0.3 6.76563 0.1875 6.94688 0.1125C7.12813 0.0375 7.3125 0 7.5 0C7.6875 0 7.87188 0.0375 8.05313 0.1125C8.23438 0.1875 8.4 0.3 8.55 0.45L14.55 6.45C14.7 6.6 14.8125 6.76563 14.8875 6.94688C14.9625 7.12813 15 7.3125 15 7.5C15 7.6875 14.9625 7.87188 14.8875 8.05313C14.8125 8.23438 14.7 8.4 14.55 8.55L8.55 14.55C8.4 14.7 8.23438 14.8125 8.05313 14.8875C7.87188 14.9625 7.6875 15 7.5 15Z" fill="currentColor"/></svg>';
					infoWindowContent += `<div><a class="js-eml-directions-link eml-infowindow__directions" href="${directionsUrl}" target="_blank" rel="noopener noreferrer" tabindex="0">Get Directions${arrowIcon}</a></div>`;
				}

				infoWindowContent += '</address></header>';

				infoWindowContent += '</div>';

				/**
				 * Setup infowindow.
				 */
				// Google's own auto-pan measures against the whole map box, which on
				// desktop runs a long way up behind the search band and behind the crop
				// above it — so a bubble up there counts as visible to Google and stays
				// hidden to the user (#30). Both paths that open a bubble position it
				// themselves against the slice that is really on screen instead:
				// `panInfowindowIntoView` on hover, `centreOnMarker` on click.
				this.infowindows[i] = new this.google.maps.InfoWindow({
					content: infoWindowContent,
					disableAutoPan: true,
				});

				/**
				 * Setup event to load the location details when the marker is clicked.
				 */
				this.google.maps.event.addListener(this.markers[i], 'click', () => {
					this.showMapLocationDetails(i);
					const markerClickEvent = new CustomEvent('emlMarkerClick', {
						detail: {
							location,
							marker: this.markers[i],
							infowindow: this.infowindows[i],
						},
					});
					window.dispatchEvent(markerClickEvent);
				});

				/**
				 * Add event to reset marker icon when infowindow is closed (without zooming out).
				 */
				this.google.maps.event.addListener(
					this.infowindows[i],
					'closeclick',
					() => {
						this.markers[i].setIcon(this.markerIcon);
						this.activeInfowindow = null;
					},
				);

				this.google.maps.event.addListener(this.markers[i], 'mouseover', () => {
					this.cancelHoverClose();
					this.markers[i].setIcon(this.markerIconHover);
					this.openHoverInfowindow(i);
				});

				this.google.maps.event.addListener(this.markers[i], 'mouseout', () => {
					this.markers[i].setIcon(this.markerIcon);
					if (
						this.hoverInfowindow
						&& this.hoverInfowindow !== this.activeInfowindow
					) {
						this.scheduleHoverClose();
					}
				});

				/**
				 * Build location list item html.
				 */
				html += location.map_item_html;
			});

			/**
			 * Fit all markers into view, unless a shortcode-supplied center is active
			 * and the user hasn't performed a search yet — in that case, honour the
			 * custom center/zoom set in initMap().
			 */
			const hasCustomCenter = !!(
				this.locatorEl.dataset.centerLat && this.locatorEl.dataset.centerLng
			);
			const hasSearched = !!(
				this.currentPosition.lat || this.currentPosition.lng
			);
			if (!hasCustomCenter || hasSearched) {
				this.map.setCenter(this.map.bounds.getCenter());
				this.fitMapBounds(this.map.bounds);
			}

			if (!this.locatorEl.classList.contains('has-results')) {
				this.locatorEl.classList.add('has-results');
			}

			if (this.cluster) {
				this.cluster.clearMarkers();
				this.cluster = false;
			}
		} else {
			html = this.getNoResultsAlert();
		}

		/**
		 * Update the mapLocationsEl html with the new html.
		 */
		this.mapLocationsEl.innerHTML = html;
		this.handleLocationsRendered();
	};

	/**
	 * Put the sidebar list back in shape after its contents were replaced.
	 *
	 * Every render path assigns to `mapLocationsEl.innerHTML`, which throws away
	 * whatever the list held — including the results summary, which is server
	 * rendered as the list's first item. Both jobs here have to run after the
	 * write, so they share one call site.
	 *
	 * @since 1.3.0
	 */
	handleLocationsRendered = () => {
		this.restoreResultsSummary();
		this.renumberPhysicianCards();
	};

	/**
	 * Re-insert the results summary at the top of the list.
	 *
	 * The element is only detached, not destroyed, so putting the same node back
	 * keeps its click handler and the text already written into it.
	 *
	 * @since 1.3.0
	 */
	restoreResultsSummary = () => {
		if (!this.mapLocationsEl || !this.resultsSummaryEl) {
			return;
		}

		this.mapLocationsEl.insertBefore(
			this.resultsSummaryEl,
			this.mapLocationsEl.firstChild,
		);
	};

	/**
	 * Number the physician cards by their position in the sidebar list.
	 *
	 * Each card's HTML is pre-rendered server-side for a single physician, so it
	 * can't know its own position. Numbering from the DOM after the list is
	 * written means the badges follow whatever order is on screen — sort changes,
	 * radius changes, load more, and cluster views all get correct numbers from
	 * the one place.
	 *
	 * @since 1.0.0
	 */
	renumberPhysicianCards = () => {
		if (!this.mapLocationsEl) {
			return;
		}

		const numberEls = this.mapLocationsEl.querySelectorAll(
			'.js-eml-physician-number',
		);

		forEach(numberEls, (i, numberEl) => {
			numberEl.textContent = i + 1;
		});
	};

	updateListLocations = () => {
		let html = '';

		if (this.locations.length > 0) {
			forEach(this.locations, (i, location) => {
				html += location.list_item_html;
			});
		} else {
			html = this.getNoResultsAlert();
		}

		this.listLocationsEl.innerHTML = html;
	};

	/**
	 * Use the results returned from the REST API to render the closest
	 * locations to the map, removing previously displayed locations.
	 *
	 * @since 1.0.0
	 */
	/**
	 * Fill in and show the results summary line.
	 *
	 * The copy templates come from data attributes on the element rather than
	 * string literals here, so they stay translatable in the PHP partial.
	 *
	 * Stays hidden until there's a search location to name — an unfiltered
	 * default list isn't "within 25 miles of" anywhere.
	 *
	 * @since 1.3.0
	 */
	updateResultsSummary = () => {
		if (!this.resultsSummaryEl) {
			return;
		}

		if (!this.searchLabel) {
			this.resultsSummaryEl.hidden = true;
			return;
		}

		const data = this.resultsSummaryEl.dataset;
		// "Showing 10 of 38": the count is what is actually in the list, so it climbs
		// with each "Load More", while the total stays the size of the whole match set.
		//
		// Deliberately not clamped to the total. Clamping is what let #40 hide: the
		// sentence read "21 of 21" while the list held more cards than that, so the
		// one number that would have shown the list and the total had come apart was
		// the number being corrected. If they ever diverge again, say so.
		const total = this.pagination.totalPosts || 0;
		const count = this.locations.length;
		const noun = total === 1 ? data.nounSingular : data.nounPlural;
		const unit = this.settings.distanceUnit;

		// No radius means the request went out unbounded, so the sentence can't
		// claim a distance.
		const template = this.currentDistance
			? data.template
			: data.templateNoRadius;

		this.resultsSummaryCountEl.textContent = template
			.replace('%count%', count)
			.replace('%total%', total)
			.replace('%noun%', noun)
			.replace('%radius%', `${this.currentDistance} ${unit}`);

		// Say what was tried first when the search had to widen itself, so the
		// radius in the sentence above doesn't look like it came from nowhere.
		if (this.widenedFrom) {
			this.resultsSummaryNoticeEl.textContent = data.templateWidened.replace(
				'%radius%',
				`${this.widenedFrom} ${unit}`,
			);
			this.resultsSummaryNoticeEl.hidden = false;
		} else {
			this.resultsSummaryNoticeEl.textContent = '';
			this.resultsSummaryNoticeEl.hidden = true;
		}

		this.resultsSummaryLocationEl.textContent = this.searchLabel;
		this.resultsSummaryEl.hidden = false;
	};

	/**
	 * Send the user back to the search input from the summary's edit button.
	 *
	 * @since 1.3.0
	 *
	 * @param {Event} e
	 */
	handleResultsSummaryEdit = (e) => {
		e.preventDefault();

		// The desktop input is the one sitting next to the summary; the mobile
		// search lives inside the map and is reached its own way.
		const searchEl = this.searchEls[0];

		if (!searchEl) {
			return;
		}

		searchEl.focus();
		searchEl.select();
	};

	handleNewLocations = () => {
		this.updateResultsSummary();

		if (this.isMapEnabled()) {
			this.updateMapLocations();
		}

		if (this.isListEnabled()) {
			this.updateListLocations();
		}

		if (this.pagination.currentPage === 1) {
			if (this.mapSidebarInnerEl) {
				this.mapSidebarInnerEl.scrollTop = 0;
			}

			if (window.innerWidth < 768) {
				if (this.isLandingPageTemplate()) {
					// Only for something the user just did, and to the module rather
					// than to the top of the document — see `scrollLocatorIntoView`.
					if (this.scrollToResultsOnNextSearch) {
						this.scrollLocatorIntoView();
					}
				} else if (this.mapLocationsEl) {
					window.scrollTo({
						top: this.mapLocationsEl.offsetTop,
						behavior: 'instant',
					});
				}
			}
		}

		this.scrollToResultsOnNextSearch = false;
	};

	/**
	 * Whether this is one of the theme's landing pages.
	 *
	 * The locator sits a long way down those templates, under a sticky header,
	 * where several behaviours written for a page that begins with the module are
	 * wrong — so they're answered differently there and left exactly as they were
	 * everywhere else.
	 *
	 * @since 1.3.0
	 *
	 * @return {Boolean} True on a landing page template.
	 */
	isLandingPageTemplate = () => document.body.classList.contains(
		'em_landing_pages-template-default',
	);

	/**
	 * Put the top of the module under the site header, so a fresh set of results on
	 * mobile arrives with the search controls, the map and the first cards all in
	 * view.
	 *
	 * Landing pages only. Elsewhere the scroll stays as it was: to
	 * `mapLocationsEl.offsetTop`, which is 0 because the results list's offset
	 * parent is the sidebar it sits in rather than the page — harmless on a page
	 * that opens with the module, and the top of a landing page is 14,000px from
	 * where the reader was (#52).
	 *
	 * @since 1.3.0
	 */
	scrollLocatorIntoView = () => {
		const siteHeader = document.querySelector('.site-header');

		const scrollToTop = () => {
			// The header is sticky, so its bottom edge is what covers the top of the
			// page once it's stuck. It also shrinks on scroll, so this is measured
			// again below from where the page has landed rather than trusted from
			// the un-scrolled position it may still be reporting here.
			const headerBottom = siteHeader
				? siteHeader.getBoundingClientRect().bottom
				: 0;
			const top =				this.locatorEl.getBoundingClientRect().top
				+ window.scrollY
				- Math.max(headerBottom, 0);

			window.scrollTo({ top: Math.max(top, 0), behavior: 'instant' });
		};

		scrollToTop();

		// Second pass for the collapsed header height, now that the page is down
		// there and the header is in its stuck state.
		window.requestAnimationFrame(scrollToTop);
	};

	handleResetClick = (e) => {
		e.preventDefault();
		this.resetMap();
	};

	/**
	 * Helper function to check if the map layout is enabled.
	 *
	 * @since 1.1.0
	 */
	isMapEnabled = () => !!this.mapContainerEl;

	/**
	 * Helper function to check if the list layout is enabled.
	 *
	 * @since 1.1.0
	 */
	isListEnabled = () => !!this.listLocationsEl;

	/**
	 * Get the current latitude coordinate.
	 *
	 * @since 1.1.0
	 */
	getLat = () => {
		if (
			this.currentPosition.lat
			|| (this.userPosition && this.userPosition.lat)
		) {
			return this.currentPosition.lat
				? this.currentPosition.lat
				: this.userPosition.lat;
		}
		return false;
	};

	/**
	 * Get the current longitude coordinate.
	 *
	 * @since 1.1.0
	 */
	getLng = () => {
		if (
			this.currentPosition.lng
			|| (this.userPosition && this.userPosition.lng)
		) {
			return this.currentPosition.lng
				? this.currentPosition.lng
				: this.userPosition.lng;
		}
		return false;
	};
}
